import {
  pgTable,
  text,
  serial,
  integer,
  boolean,
  timestamp,
  real,
  varchar,
  jsonb,
  index,
  decimal,
  unique,
  bigint,
  pgEnum,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Define enums for new fields only  
export const transactionStatusEnum = pgEnum('transaction_status', ['succeeded', 'refunded', 'failed']);
export const bookJobStatusEnum = pgEnum('book_job_status', ['pending', 'processing', 'completed', 'failed', 'cancelled']);
export const bookJobPriorityEnum = pgEnum('book_job_priority', ['low', 'normal', 'high', 'urgent']);
export const templateTypeEnum = pgEnum('template_type', ['illustrated', 'coloring', 'minimal', 'deluxe']);
export const userRoleEnum = pgEnum('user_role', ['user', 'admin', 'influencer']);
export const languageEnum = pgEnum('language', ['en', 'es', 'fr', 'de', 'zh', 'ja', 'pt', 'it', 'ru', 'ar']);
export const emailSequenceStatusEnum = pgEnum('email_sequence_status', ['pending', 'sent', 'failed', 'skipped']);

// Influencer-specific enums
export const influencerApplicationStatusEnum = pgEnum('influencer_application_status', ['draft', 'submitted', 'under_review', 'approved', 'rejected', 'needs_more_info', 'inactive']);
export const referralEventTypeEnum = pgEnum('referral_event_type', ['click', 'signup', 'subscription', 'cancellation', 'refund', 'chargeback', 'manual_adjustment', 'payout']);
export const commissionStatusEnum = pgEnum('commission_status', ['pending', 'accrued', 'paid', 'voided']);
export const mediaAssetTypeEnum = pgEnum('media_asset_type', ['media_kit', 'image_pack', 'video_pack', 'guidelines', 'misc']);

// Telemetry & Analytics Enums
export const usageEventTypeEnum = pgEnum('usage_event_type', ['story_generation', 'image_generation', 'tts_conversion', 'storage_write', 'storage_read', 'api_call', 'webhook', 'email']);
export const costMetricTypeEnum = pgEnum('cost_metric_type', ['openai_tokens', 'openai_images', 'openai_tts', 'storage_gb', 'server_compute', 'stripe_fees', 'email_sends']);
export const adjustmentStatusEnum = pgEnum('adjustment_status', ['pending', 'approved', 'rejected', 'applied', 'reverted']);
export const agentTypeEnum = pgEnum('agent_type', ['pricing_agent', 'cost_guard_agent', 'workload_forecaster', 'demand_analyzer']);
export const giftCardStatusEnum = pgEnum('gift_card_status', ['pending_payment', 'issuing', 'issued', 'failed']);

// API Keys & Mobile Devices Enums
export const apiKeyStatusEnum = pgEnum('api_key_status', ['active', 'revoked', 'expired']);
export const platformEnum = pgEnum('platform', ['ios', 'android', 'web']);

// Rate limiting tables for distributed security
export const rateLimitEntries = pgTable("rate_limit_entries", {
  id: serial("id").primaryKey(),
  key: varchar("key").notNull(),
  count: integer("count").default(1),
  resetTime: timestamp("reset_time").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_rate_limit_key").on(table.key),
  index("idx_rate_limit_reset").on(table.resetTime)
]);

export const idempotencyKeys = pgTable("idempotency_keys", {
  id: serial("id").primaryKey(),
  key: varchar("key").notNull().unique(),
  response: jsonb("response"),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_idempotency_expires").on(table.expiresAt)
]);

export const securityAuditLog = pgTable("security_audit_log", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id),
  action: varchar("action").notNull(),
  resource: varchar("resource"),
  details: jsonb("details"),
  riskLevel: varchar("risk_level").default("low"),
  ipAddress: varchar("ip_address"),
  userAgent: text("user_agent"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_audit_user").on(table.userId),
  index("idx_audit_action").on(table.action)
]);

// Mobile devices table - tracks registered mobile devices for API access
export const mobileDevices = pgTable("mobile_devices", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id),
  platform: platformEnum("platform").notNull(),
  deviceFingerprint: varchar("device_fingerprint").notNull().unique(),
  deviceName: varchar("device_name"),
  pushToken: varchar("push_token"),
  appVersion: varchar("app_version"),
  osVersion: varchar("os_version"),
  lastSeenAt: timestamp("last_seen_at"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_mobile_devices_user").on(table.userId),
  index("idx_mobile_devices_fingerprint").on(table.deviceFingerprint)
]);

// Mobile API keys table - secure API key management for mobile and third-party access
export const mobileApiKeys = pgTable("mobile_api_keys", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id),
  deviceId: integer("device_id").references(() => mobileDevices.id),
  name: varchar("name").notNull(),
  prefix: varchar("prefix").notNull().unique(),
  secretHash: varchar("secret_hash").notNull(),
  scopes: text("scopes").array().notNull().default([]),
  status: apiKeyStatusEnum("status").default("active"),
  platform: platformEnum("platform"),
  rateLimitPerMin: integer("rate_limit_per_min").default(60),
  lastUsedAt: timestamp("last_used_at"),
  expiresAt: timestamp("expires_at"),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_mobile_api_keys_user").on(table.userId),
  index("idx_mobile_api_keys_device").on(table.deviceId),
  index("idx_mobile_api_keys_prefix").on(table.prefix),
  index("idx_mobile_api_keys_status").on(table.status)
]);

// Mobile API key usage table - tracks usage analytics per mobile API key
export const mobileApiKeyUsage = pgTable("mobile_api_key_usage", {
  id: serial("id").primaryKey(),
  mobileApiKeyId: integer("mobile_api_key_id").notNull().references(() => mobileApiKeys.id),
  date: timestamp("date").notNull(),
  requestCount: integer("request_count").default(0),
  lastRequestAt: timestamp("last_request_at"),
}, (table) => [
  index("idx_mobile_api_key_usage_key_date").on(table.mobileApiKeyId, table.date)
]);

// Session storage table for Replit Auth
export const sessions = pgTable(
  "sessions",
  {
    sid: varchar("sid").primaryKey(),
    sess: jsonb("sess").notNull(),
    expire: timestamp("expire").notNull(),
  },
  (table) => [index("IDX_session_expire").on(table.expire)],
);

// Stripe webhook events audit table
export const stripeWebhookEvents = pgTable("stripe_webhook_events", {
  id: serial("id").primaryKey(),
  eventId: varchar("event_id").notNull().unique(), // Stripe event ID for idempotency
  eventType: varchar("event_type").notNull(),
  processingStatus: varchar("processing_status").notNull().default("pending"), // pending, success, error
  payloadSummary: jsonb("payload_summary"), // Key data from the event
  errorMessage: text("error_message"),
  retryCount: integer("retry_count").default(0),
  environment: varchar("environment").default("development"), // dev/prod guard
  createdAt: timestamp("created_at").defaultNow().notNull(),
  processedAt: timestamp("processed_at"),
}, (table) => [
  index("idx_stripe_event_id").on(table.eventId),
  index("idx_stripe_event_type").on(table.eventType),
  index("idx_stripe_processing_status").on(table.processingStatus)
]);

// User storage table for email/password auth
export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  email: varchar("email").notNull().unique(),
  password: text("password").notNull(),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  nickname: varchar("nickname"), // For personalized header
  gender: varchar("gender"), // girl, boy, other - for logo selection
  childGenderPreference: varchar("child_gender_preference", { length: 20 }), // Gender of the default child profile
  profilePictureUrl: varchar("profile_picture_url"), // Profile picture object storage URL
  stripeCustomerId: varchar("stripe_customer_id"),
  stripeSubscriptionId: varchar("stripe_subscription_id"),
  subscriptionStatus: varchar("subscription_status").default("trial"), // trial, active, canceled, past_due, lifetime
  subscriptionSource: varchar("subscription_source"), // coupon: LIFETIME-NEURALQUANTUM, stripe, gift, etc.
  trialEndsAt: timestamp("trial_ends_at"),
  subscriptionEndsAt: timestamp("subscription_ends_at"),
  pendingPromoCode: varchar("pending_promo_code"), // Promo code to apply when subscription is created
  pendingGiftCardCode: varchar("pending_gift_card_code"), // Gift card code to redeem after registration
  appliedPromoCode: varchar("applied_promo_code"), // Promo code currently applied to subscription
  subscriptionPeriodStartAt: timestamp("subscription_period_start_at"),
  subscriptionPeriodEndAt: timestamp("subscription_period_end_at"),
  isAdmin: boolean("is_admin").default(false), // Admin access flag
  isOfficialBearInsider: boolean("is_official_bear_insider").default(false), // Official Bear Insider verification
  role: userRoleEnum("role").default("user"), // User role: user, admin, influencer
  stripeConnectAccountId: varchar("stripe_connect_account_id"), // Stripe Connect account for influencers
  influencerData: jsonb("influencer_data").$type<{
    // Backward compatibility fields (kept for existing code)
    referralCode?: string;
    commissionRate?: number;
    isApproved?: boolean;
    totalEarnings?: number;
    pendingEarnings?: number;
    accountLink?: string;
    onboardingCompleted?: boolean;
    payoutsEnabled?: boolean;
    chargesEnabled?: boolean;
    
    // Application & Approval section
    application?: {
      date?: Date;
      status?: 'pending' | 'approved' | 'rejected' | 'under_review';
      submittedBy?: string;
      notes?: string;
      rejectionReason?: string;
      review?: {
        approvedBy?: string;
        approvedAt?: Date;
        declinedAt?: Date;
        reason?: string;
      };
      documents?: Array<{
        type: string;
        url: string;
        uploadedAt: Date;
      }>;
    };
    
    // Stripe Connect section
    stripe?: {
      accountId?: string;
      onboardingLink?: string;
      lastOnboardingSentAt?: Date;
      chargesEnabled?: boolean;
      payoutsEnabled?: boolean;
      requirements?: string[];
      defaultCommissionBps?: number;
      customCommissionOverrides?: Record<string, number>;
      dashboardUrl?: string;
      accountType?: 'express' | 'custom';
    };
    
    // Social Media section
    socials?: {
      primaryPlatform?: string;
      handles?: {
        instagram?: string;
        facebook?: string;
        tiktok?: string;
        youtube?: string;
        linkedin?: string;
        threads?: string;
        other?: Array<{ platform: string; handle: string; }>;
      };
      followers?: {
        instagram?: number;
        tiktok?: number;
        youtube?: number;
        facebook?: number;
        linkedin?: number;
      };
      engagementRate?: number;
      tier?: 'nano' | 'micro' | 'mid' | 'macro' | 'mega';
      contentCategories?: string[]; // parenting, toys, crafts, etc.
    };
    
    // Performance Metrics section
    performance?: {
      lifetime?: {
        clicks?: number;
        signups?: number;
        conversions?: number;
        grossRevenue?: number;
        netCommission?: number;
      };
      monthlyStats?: Record<string, {
        clicks?: number;
        signups?: number;
        conversions?: number;
        grossRevenue?: number;
        netCommission?: number;
        storiesGenerated?: number;
        refunds?: number;
        disputes?: number;
      }>;
      lastSyncedAt?: Date;
      lastActivityDate?: Date;
      conversionRate?: number;
    };
    
    // Media Access section
    mediaAccess?: {
      hasAcceptedBrandGuidelines?: boolean;
      brandGuidelinesAcceptedAt?: Date;
      mediaPortalEnabled?: boolean;
      downloadCounts?: {
        mediaKit?: number;
        promotionalImages?: number;
        videoAssets?: number;
        guidelinePdf?: number;
      };
      lastDownloadActivityAt?: Date;
    };
    
    // Flags and settings
    flags?: {
      featured?: boolean;
      requiresManualPayout?: boolean;
      requiresReview?: boolean;
      cooldownUntil?: Date;
      hasPendingComplianceReview?: boolean;
    };
  }>(), // Influencer-specific data
  preferredLanguage: languageEnum("preferred_language").default("en"), // User's preferred language for stories
  onboardingCompleted: boolean("onboarding_completed").default(false), // Whether user has completed onboarding
  purchasedChildProfileSlots: integer("purchased_child_profile_slots").default(0), // Number of purchased child profile slots available
  charactersGenerated: integer("characters_generated").notNull().default(0), // Number of characters generated (for quota enforcement)
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Password reset tokens
export const passwordResetTokens = pgTable("password_reset_tokens", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  token: varchar("token").notNull().unique(),
  expiresAt: timestamp("expires_at").notNull(),
  usedAt: timestamp("used_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const stories = pgTable("stories", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  childProfileId: integer("child_profile_id").references(() => childProfiles.id, { onDelete: "set null" }), // Link to child profile
  title: text("title"),
  nickname: text("nickname"), // User-assigned nickname for the story
  age: integer("age").notNull(),
  gender: text("gender").notNull(),
  childName: text("child_name"),
  interests: text("interests").array().notNull(),
  style: text("style").notNull(),
  lesson: text("lesson").notNull(),
  content: text("content").notNull(),
  language: languageEnum("language").notNull().default("en"), // Story language
  characterMap: jsonb("character_map").$type<{
    narrator?: string;
    characters?: Array<{
      name: string;
      voice: string;
      color?: string;
    }>;
  }>(), // Voice assignments for narrator and characters
  isCollaborative: boolean("is_collaborative").default(false),
  plotTwists: text("plot_twists").array(),
  hasIllustrations: boolean("has_illustrations").default(false),
  isPublic: boolean("is_public").default(false), // Whether the story is publicly accessible via shared link
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Story illustrations table - tracks individual illustrations and their placement within stories
export const storyIllustrations = pgTable("story_illustrations", {
  id: serial("id").primaryKey(),
  storyId: integer("story_id")
    .notNull()
    .references(() => stories.id, { onDelete: "cascade" }),
  sceneId: integer("scene_id").notNull(), // Scene number/order (1, 2, 3, etc.)
  placementTag: varchar("placement_tag").notNull(), // Unique identifier like "ILLUSTRATION_1", "ILLUSTRATION_2"
  insertionPoint: text("insertion_point").notNull(), // Text snippet where image should be inserted
  description: text("description").notNull(), // Human-readable scene description
  prompt: text("prompt").notNull(), // AI prompt used for generation
  imageUrl: text("image_url"), // Generated image object storage URL (nullable until generated)
  altText: text("alt_text"), // Accessibility description (nullable until generated)
  isGenerated: boolean("is_generated").default(false), // Whether illustration was successfully generated
  isFallback: boolean("is_fallback").default(false), // Whether this is a fallback placeholder
  generationMetadata: jsonb("generation_metadata"), // Additional data from generation process
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_story_illustrations_story").on(table.storyId),
  index("idx_story_illustrations_placement").on(table.placementTag),
  unique("unique_story_placement_tag").on(table.storyId, table.placementTag), // Unique placement tag per story
  index("idx_story_illustrations_scene_order").on(table.storyId, table.sceneId), // For ordering
]);

export const storyAudio = pgTable("story_audio", {
  id: serial("id").primaryKey(),
  storyId: integer("story_id")
    .notNull()
    .references(() => stories.id, { onDelete: "cascade" }),
  audioData: text("audio_data").notNull(), // Base64 encoded audio
  voice: text("voice").notNull().default("nova"),
  speed: real("speed").notNull().default(0.9),
  language: languageEnum("language").notNull().default("en"), // Audio language
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Story books table - caches generated PDF books for download
export const storyBooks = pgTable("story_books", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  storyId: integer("story_id")
    .notNull()
    .references(() => stories.id, { onDelete: "cascade" }),
  type: varchar("type").notNull(), // 'illustrated' | 'coloring'
  pdfUrl: text("pdf_url").notNull(), // Object storage URL for the PDF
  pageCount: integer("page_count").notNull(),
  bytes: bigint("bytes", { mode: "number" }).notNull(), // File size in bytes
  checksum: varchar("checksum").notNull(), // SHA-256 hash for integrity
  sourceHash: varchar("source_hash").notNull(), // Hash of story content + illustrations + layout
  layoutProfile: jsonb("layout_profile"), // Layout configuration used
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_story_books_user").on(table.userId),
  index("idx_story_books_story").on(table.storyId),
  index("idx_story_books_type").on(table.type),
  unique("unique_story_book_type").on(table.storyId, table.type), // One book per type per story
  index("idx_story_books_source_hash").on(table.sourceHash), // For cache lookup
]);

// Book generation jobs table - manages background processing with scheduling and priority
export const bookGenerationJobs = pgTable("book_generation_jobs", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  storyId: integer("story_id")
    .notNull()
    .references(() => stories.id, { onDelete: "cascade" }),
  type: varchar("type").notNull(), // 'illustrated' | 'coloring' | 'minimal' | 'deluxe'
  status: bookJobStatusEnum("status").default("pending"),
  priority: bookJobPriorityEnum("priority").default("normal"),
  progress: integer("progress").default(0), // 0-100%
  currentStep: varchar("current_step"), // 'template_generation', 'image_processing', 'pdf_creation'
  scheduledFor: timestamp("scheduled_for"), // Allow scheduling for later
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  estimatedMinutes: integer("estimated_minutes"), // Time estimate for completion
  layoutOptions: jsonb("layout_options"), // Page size, fonts, margins, etc.
  templateId: integer("template_id"), // Reference to book template used
  outputUrl: text("output_url"), // Final PDF URL when completed
  errorMessage: text("error_message"),
  retryCount: integer("retry_count").default(0),
  metadata: jsonb("metadata"), // Additional processing metadata
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_book_jobs_user").on(table.userId),
  index("idx_book_jobs_story").on(table.storyId),
  index("idx_book_jobs_status").on(table.status),
  index("idx_book_jobs_priority").on(table.priority),
  index("idx_book_jobs_scheduled").on(table.scheduledFor),
  index("idx_book_jobs_type").on(table.type),
  unique("unique_active_book_job").on(table.storyId, table.type), // Prevent duplicate jobs
]);

// Book templates table - stores markdown templates with placeholders for different book types
export const bookTemplates = pgTable("book_templates", {
  id: serial("id").primaryKey(),
  name: varchar("name").notNull(), // 'Default Illustrated', 'Minimal Coloring', etc.
  type: templateTypeEnum("type").notNull(),
  description: text("description"),
  markdownTemplate: text("markdown_template").notNull(), // Template with {{placeholders}}
  isActive: boolean("is_active").default(true),
  layoutConfig: jsonb("layout_config"), // Default layout configuration
  placeholderCount: integer("placeholder_count").default(0), // Number of image placeholders
  estimatedPages: integer("estimated_pages"), // Typical page count for this template
  previewImageUrl: text("preview_image_url"), // Template preview image
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_book_templates_type").on(table.type),
  index("idx_book_templates_active").on(table.isActive),
  unique("unique_template_name").on(table.name),
]);

// Template placeholders table - manages image placeholder mapping in templates
export const templatePlaceholders = pgTable("template_placeholders", {
  id: serial("id").primaryKey(),
  templateId: integer("template_id")
    .notNull()
    .references(() => bookTemplates.id, { onDelete: "cascade" }),
  placeholderTag: varchar("placeholder_tag").notNull(), // {{IMAGE_1}}, {{SCENE_2}}, etc.
  position: integer("position").notNull(), // Order in the template (1, 2, 3...)
  sceneDescription: text("scene_description"), // What this placeholder should show
  imageType: varchar("image_type").default("illustration"), // 'illustration' | 'outline' | 'diagram'
  isRequired: boolean("is_required").default(true), // Must be filled for complete book
  defaultImageUrl: text("default_image_url"), // Fallback image if none available
  dimensions: jsonb("dimensions"), // {width: 400, height: 300, aspectRatio: '4:3'}
  styling: jsonb("styling"), // CSS/layout styling for this placeholder
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_template_placeholders_template").on(table.templateId),
  index("idx_template_placeholders_position").on(table.position),
  index("idx_template_placeholders_tag").on(table.placeholderTag),
  unique("unique_template_placeholder").on(table.templateId, table.placeholderTag),
]);

// Job queue table - manages processing queue with worker assignment
export const jobQueue = pgTable("job_queue", {
  id: serial("id").primaryKey(),
  jobId: integer("job_id")
    .notNull()
    .references(() => bookGenerationJobs.id, { onDelete: "cascade" }),
  workerId: varchar("worker_id"), // Which worker is processing this job
  queuePosition: integer("queue_position"), // Position in queue
  estimatedStartTime: timestamp("estimated_start_time"), // When job is expected to start
  actualStartTime: timestamp("actual_start_time"),
  heartbeatAt: timestamp("heartbeat_at"), // Last worker heartbeat
  lockedUntil: timestamp("locked_until"), // Job lock expiration
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_job_queue_position").on(table.queuePosition),
  index("idx_job_queue_worker").on(table.workerId),
  index("idx_job_queue_start_time").on(table.estimatedStartTime),
  index("idx_job_queue_locked").on(table.lockedUntil),
  unique("unique_job_in_queue").on(table.jobId),
]);

// Illustration variants table - stores alternative versions of illustrations (e.g., coloring book outlines)
export const illustrationVariants = pgTable("illustration_variants", {
  id: serial("id").primaryKey(),
  storyIllustrationId: integer("story_illustration_id")
    .notNull()
    .references(() => storyIllustrations.id, { onDelete: "cascade" }),
  variant: varchar("variant").notNull(), // 'outline' for coloring books, future: 'sketch', 'watercolor', etc.
  imageUrl: text("image_url").notNull(), // Object storage URL for the variant image
  generationMethod: varchar("generation_method").notNull(), // 'filter' | 'openai'
  metadata: jsonb("metadata"), // Generation parameters, filter settings, etc.
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_illustration_variants_story_illustration").on(table.storyIllustrationId),
  index("idx_illustration_variants_variant").on(table.variant),
  unique("unique_illustration_variant").on(table.storyIllustrationId, table.variant), // One variant per type per illustration
]);

export const storyMerchandise = pgTable("story_merchandise", {
  id: serial("id").primaryKey(),
  storyId: integer("story_id")
    .notNull()
    .references(() => stories.id, { onDelete: "cascade" }),
  productType: text("product_type").notNull(), // t-shirt, mug, sticker, etc.
  productName: text("product_name").notNull(),
  description: text("description").notNull(),
  designPrompt: text("design_prompt").notNull(),
  mockupImage: text("mockup_image").notNull(), // Base64 or URL
  basePrice: real("base_price").notNull(),
  retailPrice: real("retail_price").notNull(),
  category: text("category").notNull(), // apparel, accessories, home, toys, stationery
  targetAge: text("target_age").notNull(),
  printfulProductId: text("printful_product_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Character profiles table for premium feature allowing photo uploads
export const characterProfiles = pgTable("character_profiles", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  storyId: integer("story_id")
    .references(() => stories.id, { onDelete: "set null" }), // Nullable, can be reused across stories
  photoUrl: varchar("photo_url").notNull(), // Private object storage URL
  traits: jsonb("traits"), // AI-extracted visual traits (hair color, clothing, etc.)
  promptDescription: text("prompt_description").notNull(), // AI-generated character description for prompts
  approved: boolean("approved").default(false), // User approval of AI analysis
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Cartoon characters table for premium feature
export const cartoonCharacters = pgTable("cartoon_characters", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  name: varchar("name").notNull(),
  originalPhotoUrl: text("original_photo_url"), // Original uploaded photo
  cartoonImageUrl: text("cartoon_image_url").notNull(), // Generated cartoon image
  thumbnailUrl: text("thumbnail_url"), // Smaller version for gallery
  characterTraits: jsonb("character_traits"), // Extracted character features
  promptUsed: text("prompt_used"), // The prompt used to generate the cartoon
  isActive: boolean("is_active").default(true), // Soft delete flag
  metadata: jsonb("metadata"), // Additional metadata (e.g., style preferences, age range)
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_cartoon_characters_user").on(table.userId),
  index("idx_cartoon_characters_active").on(table.isActive)
]);

// Child profiles table for managing multiple children
export const childProfiles = pgTable("child_profiles", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  name: varchar("name").notNull(), // Child's name
  birthday: timestamp("birthday"), // Child's birthday for automatic age calculation
  gender: varchar("gender"), // girl, boy, other
  preferredLanguage: varchar("preferred_language").default("en"), // Story language preference
  avatarUrl: text("avatar_url"), // Generated cartoon avatar
  originalPhotoUrl: text("original_photo_url"), // Original uploaded photo
  isDefault: boolean("is_default").default(false), // Default child for story generation
  isPurchased: boolean("is_purchased").default(false), // Whether this profile was purchased (first is free)
  purchasedAt: timestamp("purchased_at"), // When the profile was purchased
  stripePaymentIntentId: varchar("stripe_payment_intent_id"), // Stripe payment intent ID for tracking
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_child_profiles_user").on(table.userId),
  index("idx_child_profiles_default").on(table.userId, table.isDefault)
]);

// Marketplace products (SnuggleCrafters.com/VibeCaaS.com swag, handbooks, etc.)
export const marketplaceProducts = pgTable("marketplace_products", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description").notNull(),
  category: text("category").notNull(), // swag, handbook, physical_book
  price: decimal("price", { precision: 10, scale: 2 }).notNull(),
  imageUrl: text("image_url"),
  isActive: boolean("is_active").default(true),
  stockQuantity: integer("stock_quantity"),
  metadata: jsonb("metadata"), // Additional product data
  stripeProductId: varchar("stripe_product_id"), // Stripe product ID
  stripePriceId: varchar("stripe_price_id"), // Stripe price ID
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Subscription products (Character Studio, add-on subscriptions, etc.)
export const subscriptionProducts = pgTable("subscription_products", {
  id: serial("id").primaryKey(),
  name: varchar("name", { length: 255 }).notNull(),
  description: text("description").notNull(),
  stripeProductId: varchar("stripe_product_id").notNull().unique(), // Stripe product ID
  stripePriceId: varchar("stripe_price_id").notNull().unique(), // Stripe price ID
  price: decimal("price", { precision: 10, scale: 2 }).notNull(),
  currency: varchar("currency", { length: 3 }).notNull().default("USD"),
  interval: varchar("interval", { length: 20 }).notNull(), // monthly, annual, lifetime
  features: jsonb("features").$type<string[]>().default([]).notNull(), // Array of feature descriptions
  isActive: boolean("is_active").default(true).notNull(),
  sortOrder: integer("sort_order").default(0), // For display ordering
  metadata: jsonb("metadata").$type<{
    category?: string;
    imageUrl?: string;
    sku?: string;
  }>(), // Additional product metadata
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_subscription_products_active").on(table.isActive),
  index("idx_subscription_products_stripe_product").on(table.stripeProductId)
]);

// User subscription products - tracks which subscription products users have purchased
export const userSubscriptionProducts = pgTable("user_subscription_products", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  subscriptionProductId: integer("subscription_product_id")
    .notNull()
    .references(() => subscriptionProducts.id, { onDelete: "cascade" }),
  stripeSubscriptionId: varchar("stripe_subscription_id").notNull().unique(), // Stripe subscription ID
  status: varchar("status", { length: 50 }).notNull().default("active"), // active, cancelled, past_due, etc.
  currentPeriodStart: timestamp("current_period_start"),
  currentPeriodEnd: timestamp("current_period_end"),
  cancelAtPeriodEnd: boolean("cancel_at_period_end").default(false),
  canceledAt: timestamp("canceled_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_user_subscription_products_user").on(table.userId),
  index("idx_user_subscription_products_stripe_sub").on(table.stripeSubscriptionId),
  index("idx_user_subscription_products_status").on(table.status)
]);

// Orders table
export const orders = pgTable("orders", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  stripePaymentIntentId: varchar("stripe_payment_intent_id"),
  status: varchar("status").notNull().default("pending"), // pending, paid, shipped, delivered, canceled
  totalAmount: decimal("total_amount", { precision: 10, scale: 2 }).notNull(),
  shippingAddress: jsonb("shipping_address"),
  orderType: varchar("order_type").notNull(), // merchandise, marketplace, physical_book
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Order items table
export const orderItems = pgTable("order_items", {
  id: serial("id").primaryKey(),
  orderId: integer("order_id")
    .notNull()
    .references(() => orders.id),
  productType: varchar("product_type").notNull(), // story_merchandise, marketplace_product
  productId: integer("product_id").notNull(),
  storyId: integer("story_id").references(() => stories.id), // For story merchandise
  quantity: integer("quantity").notNull().default(1),
  unitPrice: decimal("unit_price", { precision: 10, scale: 2 }).notNull(),
  totalPrice: decimal("total_price", { precision: 10, scale: 2 }).notNull(),
  productData: jsonb("product_data"), // Snapshot of product at time of order
});

// User favorites table
export const favorites = pgTable(
  "favorites",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    storyId: integer("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    // Ensure a user can only favorite a story once
    unique("unique_user_story_favorite").on(table.userId, table.storyId),
  ],
);

// Story playlists for organizing collections
export const playlists = pgTable("playlists", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 255 }).notNull(),
  description: text("description"),
  isPublic: boolean("is_public").default(false),
  shareSlug: varchar("share_slug", { length: 100 }).unique(),
  coverImageUrl: varchar("cover_image_url", { length: 500 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_playlists_user").on(table.userId),
  index("idx_playlists_share_slug").on(table.shareSlug),
]);

// Stories in playlists with ordering
export const playlistStories = pgTable("playlist_stories", {
  id: serial("id").primaryKey(),
  playlistId: integer("playlist_id").notNull().references(() => playlists.id, { onDelete: "cascade" }),
  storyId: integer("story_id").notNull().references(() => stories.id, { onDelete: "cascade" }),
  position: integer("position").notNull().default(0),
  addedAt: timestamp("added_at").defaultNow().notNull(),
}, (table) => [
  unique("unique_playlist_story").on(table.playlistId, table.storyId),
  index("idx_playlist_stories_playlist").on(table.playlistId),
  index("idx_playlist_stories_story").on(table.storyId),
]);

// Segmented audio for multi-voice stories
export const storyAudioSegments = pgTable("story_audio_segments", {
  id: serial("id").primaryKey(),
  storyId: integer("story_id").notNull().references(() => stories.id, { onDelete: "cascade" }),
  segmentIndex: integer("segment_index").notNull(),
  speaker: varchar("speaker", { length: 100 }),
  voice: varchar("voice", { length: 50 }).notNull(),
  text: text("text").notNull(),
  audioData: text("audio_data").notNull(),
  startTime: real("start_time"),
  duration: real("duration"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  unique("unique_story_segment").on(table.storyId, table.segmentIndex),
  index("idx_audio_segments_story").on(table.storyId),
]);

// Email sequences for onboarding and engagement
export const emailSequences = pgTable("email_sequences", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  sequenceType: varchar("sequence_type", { length: 100 }).notNull(),
  variant: varchar("variant", { length: 10 }).default("A"),
  step: integer("step").notNull(),
  scheduledFor: timestamp("scheduled_for").notNull(),
  sentAt: timestamp("sent_at"),
  status: emailSequenceStatusEnum("status").default("pending"),
  openedAt: timestamp("opened_at"),
  clickedAt: timestamp("clicked_at"),
  trackingToken: varchar("tracking_token", { length: 100 }).unique(),
  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_email_sequences_user").on(table.userId),
  index("idx_email_sequences_scheduled").on(table.scheduledFor),
  index("idx_email_sequences_status").on(table.status),
  index("idx_email_sequences_tracking").on(table.trackingToken),
]);

// Email template variants for A/B testing
export const emailTemplateVariants = pgTable("email_template_variants", {
  id: serial("id").primaryKey(),
  sequenceType: varchar("sequence_type", { length: 100 }).notNull(),
  step: integer("step").notNull(),
  variant: varchar("variant", { length: 10 }).notNull(),
  subject: varchar("subject", { length: 255 }).notNull(),
  templateName: varchar("template_name", { length: 100 }).notNull(),
  isActive: boolean("is_active").default(true),
  conversionRate: real("conversion_rate"),
  sentCount: integer("sent_count").default(0),
  openCount: integer("open_count").default(0),
  clickCount: integer("click_count").default(0),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  unique("unique_template_variant").on(table.sequenceType, table.step, table.variant),
  index("idx_email_templates_type_step").on(table.sequenceType, table.step),
]);

// Lulu print configurations for different book types
export const luluBookConfigs = pgTable("lulu_book_configs", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(), // e.g., "Standard Storybook", "Coloring Book"
  podPackageId: varchar("pod_package_id").notNull(), // Lulu SKU
  size: varchar("size").notNull(), // e.g., "6x9"
  binding: varchar("binding").notNull(), // e.g., "Perfect Bound"
  pages: integer("pages").notNull(), // Typical page count
  color: varchar("color").notNull(), // e.g., "Full Color", "Black & White"
  basePrice: decimal("base_price", { precision: 10, scale: 2 }).notNull(), // Lulu's base cost
  retailPrice: decimal("retail_price", { precision: 10, scale: 2 }).notNull(), // Our selling price
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Lulu print jobs for tracking book orders
export const luluPrintJobs = pgTable("lulu_print_jobs", {
  id: serial("id").primaryKey(),
  orderId: integer("order_id")
    .notNull()
    .references(() => orders.id),
  storyId: integer("story_id")
    .notNull()
    .references(() => stories.id),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  luluPrintJobId: varchar("lulu_print_job_id"), // ID from Lulu API
  podPackageId: varchar("pod_package_id").notNull(),
  title: text("title").notNull(),
  coverUrl: text("cover_url").notNull(), // URL to cover PDF
  interiorUrl: text("interior_url").notNull(), // URL to interior PDF
  status: varchar("status").notNull().default("pending"), // pending, submitted, processing, shipped, delivered, failed
  shippingLevel: varchar("shipping_level").notNull().default("Mail"),
  trackingNumber: varchar("tracking_number"),
  trackingUrl: text("tracking_url"),
  luluResponse: jsonb("lulu_response"), // Full response from Lulu API
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Cache for Lulu OAuth tokens (if not using Redis)
export const luluTokens = pgTable("lulu_tokens", {
  id: serial("id").primaryKey(),
  tokenType: varchar("token_type").notNull().default("access_token"),
  token: text("token").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Reading progress tracking for bookmarking and resuming stories
export const readingProgress = pgTable(
  "reading_progress",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    storyId: integer("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    characterPosition: integer("character_position").notNull().default(0), // Position in story content
    wordIndex: integer("word_index").notNull().default(0), // Word index for highlighting
    scrollPercentage: decimal("scroll_percentage", {
      precision: 5,
      scale: 2,
    }).default("0"), // 0-100%
    lastReadAt: timestamp("last_read_at").defaultNow().notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    // Ensure one progress record per user/story combination
    unique("unique_user_story_progress").on(table.userId, table.storyId),
  ],
);

// Community stories table - tracks which stories are shared to community
export const communityStories = pgTable(
  "community_stories",
  {
    id: serial("id").primaryKey(),
    storyId: integer("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    sharedAt: timestamp("shared_at").defaultNow().notNull(),
    isActive: boolean("is_active").default(true).notNull(), // Can be hidden/removed
  },
  (table) => [
    // Ensure one entry per story in community
    index("unique_story_community").on(table.storyId),
  ],
);

// Story votes table - tracks user votes on community stories
export const storyVotes = pgTable(
  "story_votes",
  {
    id: serial("id").primaryKey(),
    storyId: integer("story_id")
      .notNull()
      .references(() => stories.id, { onDelete: "cascade" }),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    vote: integer("vote").notNull().default(1), // 1 for upvote, -1 for downvote
    votedAt: timestamp("voted_at").defaultNow().notNull(),
  },
  (table) => [
    // Ensure one vote per user per story
    unique("unique_user_story_vote").on(table.userId, table.storyId),
  ],
);

// Gift cards table
export const giftCards = pgTable("gift_cards", {
  id: serial("id").primaryKey(),
  code: varchar("code").notNull().unique(), // Unique gift card code
  purchaserUserId: integer("purchaser_user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  recipientEmail: varchar("recipient_email").notNull(),
  recipientName: varchar("recipient_name"),
  recipientUserId: integer("recipient_user_id").references(() => users.id, { onDelete: "cascade" }), // When redeemed
  subscriptionDuration: varchar("subscription_duration")
    .notNull()
    .default("monthly"), // "monthly" or "annual"
  amount: decimal("amount", { precision: 10, scale: 2 }).notNull(), // Purchase price
  message: text("message"), // Personal message from purchaser
  stripePaymentIntentId: varchar("stripe_payment_intent_id"),
  status: varchar("status").notNull().default("pending"), // pending, paid, sent, redeemed, expired
  purchasedAt: timestamp("purchased_at").defaultNow().notNull(),
  sentAt: timestamp("sent_at"), // When email was sent
  redeemedAt: timestamp("redeemed_at"), // When redeemed
  expiresAt: timestamp("expires_at"), // Optional expiration date
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Cost Analysis
export const costAnalysis = pgTable("cost_analysis", {
  id: serial("id").primaryKey(),
  analysisDate: timestamp("analysis_date").notNull().defaultNow(),
  openaiCosts: decimal("openai_costs", { precision: 10, scale: 2 })
    .notNull()
    .default("0"),
  storageCosts: decimal("storage_costs", { precision: 10, scale: 2 })
    .notNull()
    .default("0"),
  serverCosts: decimal("server_costs", { precision: 10, scale: 2 })
    .notNull()
    .default("0"),
  otherCosts: decimal("other_costs", { precision: 10, scale: 2 })
    .notNull()
    .default("0"),
  totalCosts: decimal("total_costs", { precision: 10, scale: 2 })
    .notNull()
    .default("0"),
  costBreakdown: jsonb("cost_breakdown"),
  period: varchar("period").notNull(), // 'daily', 'weekly', 'monthly'
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

// Revenue Analysis
export const revenueAnalysis = pgTable("revenue_analysis", {
  id: serial("id").primaryKey(),
  analysisDate: timestamp("analysis_date").notNull().defaultNow(),
  subscriptionRevenue: decimal("subscription_revenue", {
    precision: 10,
    scale: 2,
  })
    .notNull()
    .default("0"),
  giftCardRevenue: decimal("gift_card_revenue", { precision: 10, scale: 2 })
    .notNull()
    .default("0"),
  merchandiseRevenue: decimal("merchandise_revenue", {
    precision: 10,
    scale: 2,
  })
    .notNull()
    .default("0"),
  totalRevenue: decimal("total_revenue", { precision: 10, scale: 2 })
    .notNull()
    .default("0"),
  revenueBreakdown: jsonb("revenue_breakdown"),
  projectedRevenue: decimal("projected_revenue", { precision: 10, scale: 2 }),
  growthRate: decimal("growth_rate", { precision: 5, scale: 2 }),
  period: varchar("period").notNull(), // 'daily', 'weekly', 'monthly'
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

// Analysis Reports
export const analysisReports = pgTable("analysis_reports", {
  id: serial("id").primaryKey(),
  reportType: varchar("report_type").notNull(), // 'weekly', 'monthly'
  reportDate: timestamp("report_date").notNull().defaultNow(),
  costAnalysisId: integer("cost_analysis_id").references(() => costAnalysis.id),
  revenueAnalysisId: integer("revenue_analysis_id").references(
    () => revenueAnalysis.id,
  ),
  insights: text("insights"), // AI-generated insights
  recommendations: text("recommendations"), // AI-generated recommendations
  emailSent: boolean("email_sent").notNull().default(false),
  emailSentAt: timestamp("email_sent_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

// Coupons Table - General coupons for discounts
export const coupons = pgTable(
  "coupons",
  {
    id: serial("id").primaryKey(),
    stripeId: varchar("stripe_id").unique(), // Stripe coupon ID
    name: varchar("name").notNull(), // Coupon name
    percentOff: integer("percent_off").notNull().default(0), // Percentage discount
    amountOff: integer("amount_off").notNull().default(0), // Amount discount in cents
    currency: varchar("currency").notNull().default("usd"), // Currency
    duration: varchar("duration").notNull().default("once"), // once, repeating, forever
    durationInMonths: integer("duration_in_months"), // For repeating coupons
    maxRedemptions: integer("max_redemptions"), // Maximum number of redemptions
    timesRedeemed: integer("times_redeemed").notNull().default(0), // Current redemptions
    valid: boolean("valid").notNull().default(true), // Whether coupon is valid
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [
    index("idx_coupons_stripe_id").on(table.stripeId),
    index("idx_coupons_valid").on(table.valid),
  ],
);

// Lifetime Coupons Table
export const lifetimeCoupons = pgTable(
  "lifetime_coupons",
  {
    id: serial("id").primaryKey(),
    couponCode: varchar("coupon_code").notNull(), // e.g., "LIFETIME-NEURALQUANTUM"
    stripeCouponId: varchar("stripe_coupon_id").notNull(), // Stripe coupon ID
    email: varchar("email").notNull().unique(), // Assigned email address (unique per email)
    userId: integer("user_id").references(() => users.id, { onDelete: "cascade" }), // User who redeemed it (null if not redeemed)
    status: varchar("status").notNull().default("pending"), // pending, redeemed, revoked
    redeemedAt: timestamp("redeemed_at"),
    revokedAt: timestamp("revoked_at"),
    revokedBy: varchar("revoked_by"), // Admin who revoked it
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [
    index("idx_lifetime_coupons_email").on(table.email),
    index("idx_lifetime_coupons_status").on(table.status),
  ],
);


// Add-ons Catalog - Product catalog for illustration and storage add-ons
export const addonCatalog = pgTable("addon_catalog", {
  id: serial("id").primaryKey(),
  sku: varchar("sku").notNull().unique(), // e.g., 'illustrations_25', 'storage_5gb', 'storage_unlimited'
  type: varchar("type").notNull(), // 'illustrations' | 'storage' - keeping as varchar for now to avoid casting issues
  amountInt: integer("amount_int"), // null for unlimited storage
  price: decimal("price", { precision: 10, scale: 2 }).notNull(),
  stripeProductId: varchar("stripe_product_id"),
  stripePriceId: varchar("stripe_price_id"),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Add-ons Transactions - Purchase transaction records
export const addonTransactions = pgTable(
  "addon_transactions",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    addonId: integer("addon_id")
      .notNull()
      .references(() => addonCatalog.id, { onDelete: "cascade" }),
    quantity: integer("quantity").notNull().default(1),
    unitsGranted: integer("units_granted").notNull(), // illustrations or GB granted
    currency: varchar("currency").notNull().default("usd"),
    amountPaid: decimal("amount_paid", { precision: 10, scale: 2 }).notNull(),
    source: varchar("source").notNull(), // 'stripe', 'admin', etc.
    stripePaymentIntentId: varchar("stripe_payment_intent_id"),
    stripeChargeId: varchar("stripe_charge_id"), // For refund tracking
    status: transactionStatusEnum("status").notNull().default("succeeded"), // succeeded, refunded, failed
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    // Index for transaction history queries
    index("idx_addon_transactions_user_created").on(table.userId, table.createdAt),
  ],
);

// User Add-on Balances - Aggregated user balances
export const userAddonBalances = pgTable(
  "user_addon_balances",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    illustrationCredits: integer("illustration_credits").notNull().default(0),
    extraStorageGb: integer("extra_storage_gb").notNull().default(0),
    hasUnlimitedStorage: boolean("has_unlimited_storage").notNull().default(false),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    // Ensure one balance record per user
    unique("unique_user_addon_balance").on(table.userId),
  ],
);

// Usage Counters - Track usage per billing period
export const usageCounters = pgTable(
  "usage_counters",
  {
    id: serial("id").primaryKey(),
    userId: integer("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    periodStart: timestamp("period_start").notNull(),
    periodEnd: timestamp("period_end").notNull(),
    baseIllustrationsUsed: integer("base_illustrations_used").notNull().default(0),
    storageUsedBytes: bigint("storage_used_bytes", { mode: "number" }).notNull().default(0), // Fixed: using bigint for large storage values
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    // Ensure one usage counter per user per period
    unique("unique_user_period_usage").on(table.userId, table.periodStart),
    // Index for efficient period queries
    index("idx_usage_counters_period").on(table.periodStart, table.periodEnd),
  ],
);

export const insertUserSchema = createInsertSchema(users).pick({
  email: true,
  password: true,
  firstName: true,
  lastName: true,
  gender: true,
});

export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(6),
});

export const insertStorySchema = createInsertSchema(stories).omit({
  id: true,
  createdAt: true,
});

export const storyGenerationSchema = z.object({
  age: z.number().min(0).max(12),
  gender: z.enum(["girl", "boy", "other"]),
  language: z.string().optional().default("en"), // Language for story generation (en, es, fr, de, zh, ja, pt, it, ru, ar)
  childName: z.string().min(1).optional(),
  childProfileId: z.number().optional().nullable(), // ID of selected child profile
  interests: z.array(z.string()).min(1).max(3),
  style: z.enum(["funny", "adventurous", "gentle", "magical", "educational"]),
  lesson: z.string().min(1),
  length: z.enum(["short", "long"]).optional().default("long"), // Story length preference
  isCollaborative: z.boolean().optional(),
  plotTwists: z.array(z.string()).optional(),
  enableIllustrations: z.boolean().optional().default(false),
  characterImageUrl: z.string().optional(), // URL of uploaded character image
  characterImageData: z.object({
    originalUrl: z.string().optional(),
    processedUrl: z.string().optional(),
    characterFeatures: z.any().optional(), // Character features extracted from image
    characterPrompt: z.string().optional(),
  }).optional().nullable(), // Character image data for custom illustrations
});

export const insertCharacterProfileSchema = createInsertSchema(characterProfiles).omit({
  id: true,
  createdAt: true,
});

// Child profile schemas
export const insertChildProfileSchema = createInsertSchema(childProfiles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Cartoon character types and schemas
export type CartoonCharacter = typeof cartoonCharacters.$inferSelect;
export const cartoonCharacterInsertSchema = createInsertSchema(cartoonCharacters);

export const cartoonCharacterGenerationSchema = z.object({
  name: z.string().min(1).max(100),
  photoBase64: z.string(), // Base64 encoded image
  style: z.enum(['storybook', 'pixar', 'anime', 'classic']).optional().default('storybook'),
  ageRange: z.enum(['3-5', '6-8', '9-10']).optional().default('3-5'),
});

export type CartoonCharacterGenerationRequest = z.infer<typeof cartoonCharacterGenerationSchema>;

export type InsertUser = z.infer<typeof insertUserSchema>;
export type LoginUser = z.infer<typeof loginSchema>;
export type User = typeof users.$inferSelect;
export type InsertStory = z.infer<typeof insertStorySchema>;
export type Story = typeof stories.$inferSelect;
export type StoryGenerationRequest = z.infer<typeof storyGenerationSchema>;
export type CharacterProfile = typeof characterProfiles.$inferSelect;
export type InsertCharacterProfile = z.infer<typeof insertCharacterProfileSchema>;
export type ChildProfile = typeof childProfiles.$inferSelect;
export type InsertChildProfile = z.infer<typeof insertChildProfileSchema>;
export type MarketplaceProduct = typeof marketplaceProducts.$inferSelect;
export type Order = typeof orders.$inferSelect;
export type OrderItem = typeof orderItems.$inferSelect;
export type LuluBookConfig = typeof luluBookConfigs.$inferSelect;
export type LuluPrintJob = typeof luluPrintJobs.$inferSelect;
export type LuluToken = typeof luluTokens.$inferSelect;
export type ReadingProgress = typeof readingProgress.$inferSelect;
export type CommunityStory = typeof communityStories.$inferSelect;
export type StoryVote = typeof storyVotes.$inferSelect;
export type GiftCard = typeof giftCards.$inferSelect;
export type CostAnalysis = typeof costAnalysis.$inferSelect;
export type RevenueAnalysis = typeof revenueAnalysis.$inferSelect;
export type AnalysisReport = typeof analysisReports.$inferSelect;
export type LifetimeCoupon = typeof lifetimeCoupons.$inferSelect;
export type AddonCatalog = typeof addonCatalog.$inferSelect;
export type AddonTransaction = typeof addonTransactions.$inferSelect;
export type UserAddonBalances = typeof userAddonBalances.$inferSelect;
export type UsageCounters = typeof usageCounters.$inferSelect;

export const lifetimeCouponInsertSchema = createInsertSchema(
  lifetimeCoupons,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertLifetimeCoupon = z.infer<typeof lifetimeCouponInsertSchema>;

// Add-ons system insert schemas
export const addonCatalogInsertSchema = createInsertSchema(addonCatalog).omit({
  id: true,
  createdAt: true,
});

export const addonTransactionInsertSchema = createInsertSchema(addonTransactions).omit({
  id: true,
  createdAt: true,
});

export const userAddonBalancesInsertSchema = createInsertSchema(userAddonBalances).omit({
  id: true,
  updatedAt: true,
});

export const usageCountersInsertSchema = createInsertSchema(usageCounters).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Add-ons system insert types
export type InsertAddonCatalog = z.infer<typeof addonCatalogInsertSchema>;
export type InsertAddonTransaction = z.infer<typeof addonTransactionInsertSchema>;
export type InsertUserAddonBalances = z.infer<typeof userAddonBalancesInsertSchema>;
export type InsertUsageCounters = z.infer<typeof usageCountersInsertSchema>;

// Merchandise Products Schema
export const merchandiseProducts = pgTable("merchandise_products", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  slug: text("slug").notNull().unique(),
  productType: text("product_type").notNull(), // t-shirt, hoodie, mug, tote, etc.
  category: text("category").notNull(), // apparel, accessories, stationery
  description: text("description").notNull(),
  price: decimal("price", { precision: 10, scale: 2 }).notNull(),
  images: text("images").array().notNull(), // Array of image URLs
  features: text("features").array(), // Bullet points for product features
  sizes: text("sizes").array(), // Available sizes
  colors: text("colors").array(), // Available colors
  inStock: boolean("in_stock").notNull().default(true),
  featured: boolean("featured").notNull().default(false),
  sortOrder: integer("sort_order").notNull().default(0),
  metadata: jsonb("metadata"), // Additional product info
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Pricing Intelligence System Tables
export const pricingProducts = pgTable("pricing_products", {
  id: serial("id").primaryKey(),
  productKey: varchar("product_key").notNull().unique(), // e.g., 'premium_monthly', 'character_studio_annual'
  productName: text("product_name").notNull(),
  productType: varchar("product_type").notNull(), // 'subscription', 'addon', 'whiteLabelPlans', 'apiPlans'
  currentPrice: decimal("current_price", { precision: 10, scale: 2 }).notNull(),
  costPerCustomer: decimal("cost_per_customer", { precision: 10, scale: 2 }).notNull(),
  marginPercent: decimal("margin_percent", { precision: 5, scale: 2 }).notNull(),
  filePath: text("file_path").notNull(), // Path to file containing pricing definition
  codeLineRef: integer("code_line_ref"), // Line number where price is defined
  stripeProductId: varchar("stripe_product_id"),
  stripePriceId: varchar("stripe_price_id"),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const pricingRecommendations = pgTable("pricing_recommendations", {
  id: serial("id").primaryKey(),
  productId: integer("product_id").notNull().references(() => pricingProducts.id, { onDelete: "cascade" }),
  recommendedPrice: decimal("recommended_price", { precision: 10, scale: 2 }).notNull(),
  currentPrice: decimal("current_price", { precision: 10, scale: 2 }).notNull(),
  priceChangePercent: decimal("price_change_percent", { precision: 5, scale: 2 }).notNull(),
  reasoning: text("reasoning").notNull(),
  priority: varchar("priority").notNull().default("medium"), // low, medium, high, urgent
  impactAnalysis: jsonb("impact_analysis"), // Expected revenue impact, customer response, etc.
  approvalStatus: varchar("approval_status").default("pending"), // pending, approved, rejected, implemented
  approvedBy: varchar("approved_by"), // Craig Ross or user approval
  implementedAt: timestamp("implemented_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_pricing_recommendations_status").on(table.approvalStatus),
  index("idx_pricing_recommendations_priority").on(table.priority)
]);

export const intelligenceReports = pgTable("intelligence_reports", {
  id: serial("id").primaryKey(),
  reportType: varchar("report_type").notNull(), // 'weekly_pricing', 'cost_analysis', 'competitive_analysis'
  reportTitle: text("report_title").notNull(),
  executiveSummary: text("executive_summary").notNull(),
  keyMetrics: jsonb("key_metrics").notNull(),
  insights: text("insights").notNull(),
  recommendations: text("recommendations").notNull(),
  dataSnapshot: jsonb("data_snapshot").notNull(), // Full data used for analysis
  generatedBy: varchar("generated_by").notNull().default("ai"), // ai, user, system
  sharedWithCraig: boolean("shared_with_craig").default(false),
  craigFeedback: text("craig_feedback"),
  actionItems: jsonb("action_items"), // List of action items with status
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_intelligence_reports_type").on(table.reportType),
  index("idx_intelligence_reports_created").on(table.createdAt)
]);

export const workloadProfiles = pgTable("workload_profiles", {
  id: serial("id").primaryKey(),
  profileName: varchar("profile_name").notNull().unique(),
  userSegment: varchar("user_segment").notNull(), // trial, premium_monthly, premium_yearly, character_studio
  avgStoriesPerMonth: integer("avg_stories_per_month").notNull(),
  avgAudioPerMonth: integer("avg_audio_per_month").notNull(),
  avgImagesPerMonth: integer("avg_images_per_month").notNull(),
  avgStorageUsageGB: decimal("avg_storage_usage_gb", { precision: 5, scale: 2 }).notNull(),
  avgServerHoursPerMonth: decimal("avg_server_hours_per_month", { precision: 6, scale: 2 }).notNull(),
  mlPredictedGrowth: decimal("ml_predicted_growth", { precision: 5, scale: 2 }).default("0"), // ML predicted growth %
  costPerUser: decimal("cost_per_user", { precision: 10, scale: 4 }).notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_workload_profiles_segment").on(table.userSegment)
]);

export const costPredictions = pgTable("cost_predictions", {
  id: serial("id").primaryKey(),
  predictionDate: timestamp("prediction_date").notNull(),
  horizon: varchar("horizon").notNull(), // '30_days', '90_days', '1_year'
  userSegment: varchar("user_segment").notNull(),
  predictedUsers: integer("predicted_users").notNull(),
  predictedCosts: decimal("predicted_costs", { precision: 10, scale: 2 }).notNull(),
  predictedRevenue: decimal("predicted_revenue", { precision: 10, scale: 2 }).notNull(),
  confidenceScore: decimal("confidence_score", { precision: 3, scale: 2 }).notNull(), // 0.00-1.00
  modelVersion: varchar("model_version").notNull().default("v1"),
  actualCosts: decimal("actual_costs", { precision: 10, scale: 2 }), // Filled in later for accuracy tracking
  actualRevenue: decimal("actual_revenue", { precision: 10, scale: 2 }),
  accuracyScore: decimal("accuracy_score", { precision: 3, scale: 2 }), // How accurate the prediction was
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_cost_predictions_date").on(table.predictionDate),
  index("idx_cost_predictions_horizon").on(table.horizon)
]);

export const merchandiseProductInsertSchema = createInsertSchema(
  merchandiseProducts,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type MerchandiseProduct = typeof merchandiseProducts.$inferSelect;
export type InsertMerchandiseProduct = z.infer<
  typeof merchandiseProductInsertSchema
>;

// Influencer Applications Schema - comprehensive application tracking
export const influencerApplications = pgTable("influencer_applications", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .references(() => users.id, { onDelete: "cascade" }), // Optional - can apply without account
  fullName: text("full_name").notNull(),
  email: varchar("email").notNull(),
  platforms: text("platforms").array().notNull(), // instagram, tiktok, youtube, blog, etc.
  audienceSize: integer("audience_size").notNull(),
  socialHandles: jsonb("social_handles").notNull(), // { platform: handle } mapping
  followerCounts: jsonb("follower_counts"), // { platform: count } mapping
  engagementRate: decimal("engagement_rate", { precision: 5, scale: 2 }), // e.g., 3.45%
  contentTypes: text("content_types").array().notNull(), // parenting, lifestyle, education, etc.
  primaryPlatform: varchar("primary_platform"),
  previousCollaborations: text("previous_collaborations"),
  whySnuggleCrafters: text("why_snuggle_crafters").notNull(),
  proposedContent: text("proposed_content"),
  availableStartDate: timestamp("available_start_date"),
  preferredCompensation: text("preferred_compensation"), // free_subscription, commission, flat_rate, product_gifting
  websiteUrl: text("website_url"),
  mediaKit: text("media_kit"), // URL to media kit or portfolio
  status: influencerApplicationStatusEnum("status").notNull().default("submitted"),
  reviewNotes: text("review_notes"), // Admin notes for review process
  reviewedBy: integer("reviewed_by").references(() => users.id), // Admin who reviewed
  reviewedAt: timestamp("reviewed_at"),
  approvedCommissionBps: integer("approved_commission_bps").default(1500), // 15% in basis points
  stripeConnectStatus: varchar("stripe_connect_status"), // Status of Stripe onboarding
  followupDate: timestamp("followup_date"),
  metadata: jsonb("metadata"), // Additional data as needed
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_influencer_applications_user").on(table.userId),
  index("idx_influencer_applications_status").on(table.status),
  index("idx_influencer_applications_email").on(table.email),
]);

// Influencer Referrals tracking - comprehensive referral and commission tracking
export const influencerReferrals = pgTable("influencer_referrals", {
  id: serial("id").primaryKey(),
  influencerId: integer("influencer_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  referredUserId: integer("referred_user_id")
    .references(() => users.id, { onDelete: "set null" }),
  promoCodeId: integer("promo_code_id")
    .references(() => influencerPromoCodes.id, { onDelete: "set null" }), // Reference to promo code
  referralCode: varchar("referral_code").notNull(),
  externalId: varchar("external_id").unique(), // External reference ID (e.g., Stripe payment intent)
  eventType: referralEventTypeEnum("event_type").notNull().default("click"),
  conversions: integer("conversions").default(0),
  grossRevenue: decimal("gross_revenue", { precision: 12, scale: 2 }).default("0"),
  commissionAmount: decimal("commission_amount", { precision: 12, scale: 2 }).default("0"),
  commissionStatus: commissionStatusEnum("commission_status").default("pending"),
  stripeTransferId: varchar("stripe_transfer_id"), // Stripe transfer ID for payout tracking
  storyCount: integer("story_count").default(0),
  notes: text("notes"),
  occurredAt: timestamp("occurred_at").defaultNow().notNull(),
  metadata: jsonb("metadata"), // Additional tracking data
}, (table) => [
  index("idx_referrals_influencer").on(table.influencerId),
  index("idx_referrals_code").on(table.referralCode),
  index("idx_referrals_event_time").on(table.eventType, table.occurredAt),
  index("idx_referrals_commission_status").on(table.commissionStatus),
  unique("unique_referral_event").on(table.influencerId, table.referredUserId, table.eventType, table.occurredAt), // Prevent duplicate events
]);

// Influencer Media Access Log - tracks downloads of marketing materials and brand assets  
export const influencerMediaAccess = pgTable("influencer_media_access", {
  id: serial("id").primaryKey(),
  influencerId: integer("influencer_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  assetSlug: varchar("asset_slug").notNull(), // Identifier for the asset (e.g., 'media-kit-2024', 'logo-pack')
  assetType: mediaAssetTypeEnum("asset_type").notNull(),
  downloadUrl: text("download_url"), // The URL that was accessed
  downloadedAt: timestamp("downloaded_at").defaultNow().notNull(),
  ipAddress: varchar("ip_address"), // IP address for security tracking
  userAgent: text("user_agent"), // Browser/device information
  metadata: jsonb("metadata"), // Additional context (e.g., version, format)
}, (table) => [
  index("idx_media_access_influencer").on(table.influencerId),
  index("idx_media_access_asset").on(table.assetSlug),
  index("idx_media_access_type").on(table.assetType),
  index("idx_media_access_date").on(table.downloadedAt),
]);

// Influencer Promo Codes - comprehensive promo code management for influencers
export const influencerPromoCodes = pgTable("influencer_promo_codes", {
  id: serial("id").primaryKey(),
  influencerId: integer("influencer_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  stripePromotionCodeId: varchar("stripe_promotion_code_id").unique(),
  stripeCouponId: varchar("stripe_coupon_id"), // Reference to Stripe coupon
  code: varchar("code", { length: 100 }).notNull().unique(),
  description: text("description"),
  discountPercent: integer("discount_percent").default(10), // 10% default discount
  commissionBps: integer("commission_bps").default(1500), // 15% commission in basis points
  status: varchar("status", { length: 20 }).default("active"), // active, inactive, expired
  maxRedemptions: integer("max_redemptions"),
  timesRedeemed: integer("times_redeemed").default(0),
  totalRevenue: decimal("total_revenue", { precision: 12, scale: 2 }).default("0"),
  totalCommissionEarned: decimal("total_commission_earned", { precision: 12, scale: 2 }).default("0"),
  expiresAt: timestamp("expires_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  metadata: jsonb("metadata"), // Additional data like campaign tags, notes
}, (table) => [
  index("idx_promo_codes_influencer").on(table.influencerId),
  index("idx_promo_codes_code").on(table.code),
  index("idx_promo_codes_status").on(table.status),
  index("idx_promo_codes_stripe").on(table.stripePromotionCodeId),
]);

export const influencerApplicationInsertSchema = createInsertSchema(
  influencerApplications,
).omit({
  id: true,
  status: true,
  reviewNotes: true,
  reviewedBy: true,
  reviewedAt: true,
  createdAt: true,
  updatedAt: true,
});

export type InfluencerApplication = typeof influencerApplications.$inferSelect;
export type InsertInfluencerApplication = z.infer<
  typeof influencerApplicationInsertSchema
>;

// Influencer Referrals Schemas
export const influencerReferralInsertSchema = createInsertSchema(
  influencerReferrals,
).omit({
  id: true,
  occurredAt: true,
});

export type InfluencerReferral = typeof influencerReferrals.$inferSelect;
export type InsertInfluencerReferral = z.infer<
  typeof influencerReferralInsertSchema
>;

// Influencer Media Access Schemas
export const influencerMediaAccessInsertSchema = createInsertSchema(
  influencerMediaAccess,
).omit({
  id: true,
  downloadedAt: true,
});

export type InfluencerMediaAccess = typeof influencerMediaAccess.$inferSelect;
export type InsertInfluencerMediaAccess = z.infer<
  typeof influencerMediaAccessInsertSchema
>;

// Influencer Promo Codes Schemas
export const influencerPromoCodeInsertSchema = createInsertSchema(
  influencerPromoCodes,
).omit({
  id: true,
  timesRedeemed: true,
  totalRevenue: true,
  totalCommissionEarned: true,
  createdAt: true,
  updatedAt: true,
});

export type InfluencerPromoCode = typeof influencerPromoCodes.$inferSelect;
export type InsertInfluencerPromoCode = z.infer<
  typeof influencerPromoCodeInsertSchema
>;

// Developer Portal Schema
export const developerAccounts = pgTable("developer_accounts", {
  id: serial("id").primaryKey(),
  userId: integer("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  companyName: varchar("company_name"),
  website: varchar("website"),
  description: text("description"),
  useCase: text("use_case"), // How they plan to use the API
  status: varchar("status").notNull().default("pending"), // pending, approved, rejected, suspended
  approvedAt: timestamp("approved_at"),
  approvedBy: integer("approved_by").references(() => users.id), // Admin who approved
  rejectedAt: timestamp("rejected_at"),
  rejectedBy: integer("rejected_by").references(() => users.id), // Admin who rejected
  rejectionReason: text("rejection_reason"),
  tier: varchar("tier").notNull().default("free"), // free, pro, enterprise
  monthlyLimit: integer("monthly_limit").notNull().default(1000), // API calls per month
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const apiKeys = pgTable("api_keys", {
  id: serial("id").primaryKey(),
  developerAccountId: integer("developer_account_id")
    .notNull()
    .references(() => developerAccounts.id, { onDelete: "cascade" }),
  keyName: varchar("key_name").notNull(), // User-defined name for the key
  keyHash: varchar("key_hash").notNull().unique(), // Hashed version of the API key
  keyPrefix: varchar("key_prefix").notNull(), // First 8 characters for identification
  permissions: jsonb("permissions").notNull().default("{}"), // JSON object with permission flags
  isActive: boolean("is_active").notNull().default(true),
  lastUsedAt: timestamp("last_used_at"),
  expiresAt: timestamp("expires_at"), // Optional expiration date
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const apiUsage = pgTable("api_usage", {
  id: serial("id").primaryKey(),
  apiKeyId: integer("api_key_id")
    .notNull()
    .references(() => apiKeys.id, { onDelete: "cascade" }),
  endpoint: varchar("endpoint").notNull(), // API endpoint called
  method: varchar("method").notNull(), // HTTP method
  statusCode: integer("status_code").notNull(),
  responseTime: integer("response_time"), // Response time in milliseconds
  requestSize: integer("request_size"), // Request size in bytes
  responseSize: integer("response_size"), // Response size in bytes
  userAgent: text("user_agent"),
  ipAddress: varchar("ip_address"),
  errorMessage: text("error_message"), // If request failed
  createdAt: timestamp("created_at").defaultNow(),
});

export const apiPlans = pgTable("api_plans", {
  id: serial("id").primaryKey(),
  name: varchar("name").notNull().unique(),
  description: text("description").notNull(),
  tier: varchar("tier").notNull(), // free, pro, enterprise
  monthlyLimit: integer("monthly_limit").notNull(),
  price: decimal("price", { precision: 10, scale: 2 }).notNull().default("0"),
  features: jsonb("features").notNull().default("[]"), // Array of feature strings
  isActive: boolean("is_active").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// API Endpoints Inventory - Catalog of all available API endpoints
export const apiEndpoints = pgTable("api_endpoints", {
  id: serial("id").primaryKey(),
  name: varchar("name").notNull(), // Human-readable name
  path: varchar("path").notNull(), // e.g., /api/v1/stories/generate
  method: varchar("method").notNull(), // GET, POST, PUT, DELETE
  description: text("description").notNull(),
  category: varchar("category").notNull(), // stories, characters, audio, illustrations
  version: varchar("version").notNull().default("v1"),
  requestSchema: jsonb("request_schema"), // JSON schema for request body
  responseSchema: jsonb("response_schema"), // JSON schema for response
  exampleRequest: jsonb("example_request"), // Example request body
  exampleResponse: jsonb("example_response"), // Example response body
  rateLimitPerMinute: integer("rate_limit_per_minute").notNull().default(60),
  costPerCall: decimal("cost_per_call", { precision: 10, scale: 4 }).default("0"), // Cost per API call
  requiredPermission: varchar("required_permission").notNull(), // Permission required to access
  isActive: boolean("is_active").notNull().default(true),
  isPublic: boolean("is_public").notNull().default(true), // Whether endpoint is publicly documented
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_api_endpoints_category").on(table.category),
  index("idx_api_endpoints_version").on(table.version),
  index("idx_api_endpoints_active").on(table.isActive),
  unique("unique_endpoint_path_method").on(table.path, table.method),
]);

// API Entitlements - Maps plan tiers to endpoint permissions
export const apiEntitlements = pgTable("api_entitlements", {
  id: serial("id").primaryKey(),
  planId: integer("plan_id")
    .notNull()
    .references(() => apiPlans.id, { onDelete: "cascade" }),
  endpointId: integer("endpoint_id")
    .notNull()
    .references(() => apiEndpoints.id, { onDelete: "cascade" }),
  isAllowed: boolean("is_allowed").notNull().default(true),
  rateLimitOverride: integer("rate_limit_override"), // Override default rate limit
  dailyLimit: integer("daily_limit"), // Daily call limit for this endpoint
  monthlyLimit: integer("monthly_limit"), // Monthly call limit for this endpoint
  metadata: jsonb("metadata"), // Additional entitlement data
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_api_entitlements_plan").on(table.planId),
  index("idx_api_entitlements_endpoint").on(table.endpointId),
  unique("unique_plan_endpoint").on(table.planId, table.endpointId),
]);

// API Key Scopes - Tracks which permissions are assigned to each API key
export const apiKeyScopes = pgTable("api_key_scopes", {
  id: serial("id").primaryKey(),
  apiKeyId: integer("api_key_id")
    .notNull()
    .references(() => apiKeys.id, { onDelete: "cascade" }),
  endpointId: integer("endpoint_id")
    .notNull()
    .references(() => apiEndpoints.id, { onDelete: "cascade" }),
  isEnabled: boolean("is_enabled").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_api_key_scopes_key").on(table.apiKeyId),
  index("idx_api_key_scopes_endpoint").on(table.endpointId),
  unique("unique_key_endpoint_scope").on(table.apiKeyId, table.endpointId),
]);

export const developerSupportTickets = pgTable("developer_support_tickets", {
  id: serial("id").primaryKey(),
  developerAccountId: integer("developer_account_id")
    .notNull()
    .references(() => developerAccounts.id, { onDelete: "cascade" }),
  subject: varchar("subject").notNull(),
  description: text("description").notNull(),
  priority: varchar("priority").notNull().default("medium"), // low, medium, high, urgent
  status: varchar("status").notNull().default("open"), // open, in_progress, resolved, closed
  assignedTo: integer("assigned_to").references(() => users.id), // Admin assigned to ticket
  resolution: text("resolution"), // Resolution notes
  resolvedAt: timestamp("resolved_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const developerSupportMessages = pgTable("developer_support_messages", {
  id: serial("id").primaryKey(),
  ticketId: integer("ticket_id")
    .notNull()
    .references(() => developerSupportTickets.id, { onDelete: "cascade" }),
  senderId: integer("sender_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  message: text("message").notNull(),
  isInternal: boolean("is_internal").notNull().default(false), // Internal admin notes
  createdAt: timestamp("created_at").defaultNow(),
});

// Upload Sessions - For secure upload tracking and validation
export const uploadStatusEnum = pgEnum('upload_status', ['pending', 'completed', 'failed', 'expired']);

export const uploadSessions = pgTable("upload_sessions", {
  id: serial("id").primaryKey(),
  uploadId: varchar("upload_id").notNull().unique(), // UUID for the upload session
  userId: integer("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  objectId: varchar("object_id").notNull(), // Object storage object ID
  fileName: varchar("file_name"), // Original filename
  expectedSize: bigint("expected_size", { mode: "number" }).notNull(), // Expected file size in bytes
  actualSize: bigint("actual_size", { mode: "number" }), // Actual uploaded size (null until completed)
  status: uploadStatusEnum("status").notNull().default("pending"),
  expiresAt: timestamp("expires_at").notNull(), // TTL for session cleanup
  completedAt: timestamp("completed_at"), // When the upload was completed
  failureReason: text("failure_reason"), // Error message if failed
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  // Index for efficient cleanup of expired sessions
  index("idx_upload_sessions_expires").on(table.expiresAt),
  // Index for user-specific queries
  index("idx_upload_sessions_user_created").on(table.userId, table.createdAt),
  // Index for status-based queries
  index("idx_upload_sessions_status").on(table.status),
]);

// Schema validation types
export const developerAccountInsertSchema = createInsertSchema(developerAccounts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const apiKeyInsertSchema = createInsertSchema(apiKeys).omit({
  id: true,
  keyHash: true,
  createdAt: true,
  updatedAt: true,
});

export const apiPlanInsertSchema = createInsertSchema(apiPlans).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const supportTicketInsertSchema = createInsertSchema(developerSupportTickets).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const supportMessageInsertSchema = createInsertSchema(developerSupportMessages).omit({
  id: true,
  createdAt: true,
});

export const uploadSessionInsertSchema = createInsertSchema(uploadSessions).omit({
  id: true,
  createdAt: true,
});

// Story illustrations schema and types
export const storyIllustrationInsertSchema = createInsertSchema(storyIllustrations).omit({
  id: true,
  createdAt: true,
});

export type StoryIllustration = typeof storyIllustrations.$inferSelect;
export type InsertStoryIllustration = z.infer<typeof storyIllustrationInsertSchema>;

// Story books schema and types
export const storyBookInsertSchema = createInsertSchema(storyBooks).omit({
  id: true,
  createdAt: true,
});

export type StoryBook = typeof storyBooks.$inferSelect;
export type InsertStoryBook = z.infer<typeof storyBookInsertSchema>;

// Illustration variants schema and types
export const illustrationVariantInsertSchema = createInsertSchema(illustrationVariants).omit({
  id: true,
  createdAt: true,
});

export type IllustrationVariant = typeof illustrationVariants.$inferSelect;
export type InsertIllustrationVariant = z.infer<typeof illustrationVariantInsertSchema>;

// API Endpoints and Entitlements Insert Schemas
export const apiEndpointInsertSchema = createInsertSchema(apiEndpoints).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const apiEntitlementInsertSchema = createInsertSchema(apiEntitlements).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const apiKeyScopeInsertSchema = createInsertSchema(apiKeyScopes).omit({
  id: true,
  createdAt: true,
});

// Type exports
export type DeveloperAccount = typeof developerAccounts.$inferSelect;
export type InsertDeveloperAccount = z.infer<typeof developerAccountInsertSchema>;
export type ApiKey = typeof apiKeys.$inferSelect;
export type InsertApiKey = z.infer<typeof apiKeyInsertSchema>;
export type ApiUsage = typeof apiUsage.$inferSelect;
export type ApiPlan = typeof apiPlans.$inferSelect;
export type InsertApiPlan = z.infer<typeof apiPlanInsertSchema>;
export type ApiEndpoint = typeof apiEndpoints.$inferSelect;
export type InsertApiEndpoint = z.infer<typeof apiEndpointInsertSchema>;
export type ApiEntitlement = typeof apiEntitlements.$inferSelect;
export type InsertApiEntitlement = z.infer<typeof apiEntitlementInsertSchema>;
export type ApiKeyScope = typeof apiKeyScopes.$inferSelect;
export type InsertApiKeyScope = z.infer<typeof apiKeyScopeInsertSchema>;
export type DeveloperSupportTicket = typeof developerSupportTickets.$inferSelect;
export type InsertDeveloperSupportTicket = z.infer<typeof supportTicketInsertSchema>;
export type DeveloperSupportMessage = typeof developerSupportMessages.$inferSelect;
export type InsertDeveloperSupportMessage = z.infer<typeof supportMessageInsertSchema>;
export type UploadSession = typeof uploadSessions.$inferSelect;
export type InsertUploadSession = z.infer<typeof uploadSessionInsertSchema>;

// Stripe webhook events schema and types
export const stripeWebhookEventInsertSchema = createInsertSchema(stripeWebhookEvents).omit({
  id: true,
  createdAt: true,
});

export type StripeWebhookEvent = typeof stripeWebhookEvents.$inferSelect;
export type InsertStripeWebhookEvent = z.infer<typeof stripeWebhookEventInsertSchema>;

// Help System Schemas (Zod-only, no database tables needed)
export const articleSectionSchema = z.object({
  kind: z.enum(['p', 'h2', 'steps', 'tip', 'warning', 'faq']),
  content: z.union([z.string(), z.array(z.string())]),
});

export const helpArticleMetaSchema = z.object({
  slug: z.string(),
  title: z.string(),
  description: z.string(),
  type: z.enum(['guide', 'troubleshooting']),
  difficulty: z.enum(['beginner', 'intermediate', 'advanced']),
  estimatedTime: z.string(),
});

export const helpArticleSchema = helpArticleMetaSchema.extend({
  categorySlug: z.string(),
  body: z.array(articleSectionSchema),
  meta: z.object({
    titleTag: z.string(),
    description: z.string(),
    ogImage: z.string().optional(),
  }),
});

export const helpCategorySchema = z.object({
  slug: z.string(),
  title: z.string(),
  iconKey: z.string(),
  articles: z.array(helpArticleMetaSchema),
});

// Character photo analysis schema for character generation
export const characterPhotoAnalysisSchema = z.object({
  base64Image: z.string(),
  characterName: z.string().optional(),
  analysisType: z.enum(['traits', 'cartoon']).default('traits'),
});

export type CharacterPhotoAnalysis = z.infer<typeof characterPhotoAnalysisSchema>;

// Type exports for help system
export type ArticleSection = z.infer<typeof articleSectionSchema>;
export type HelpArticleMeta = z.infer<typeof helpArticleMetaSchema>;
export type HelpArticle = z.infer<typeof helpArticleSchema>;
export type HelpCategory = z.infer<typeof helpCategorySchema>;

// Book generation system schemas and types
export const bookGenerationJobInsertSchema = createInsertSchema(bookGenerationJobs).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const bookTemplateInsertSchema = createInsertSchema(bookTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Pricing Intelligence System Insert Schemas
export const pricingProductInsertSchema = createInsertSchema(pricingProducts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const pricingRecommendationInsertSchema = createInsertSchema(pricingRecommendations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const intelligenceReportInsertSchema = createInsertSchema(intelligenceReports).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const workloadProfileInsertSchema = createInsertSchema(workloadProfiles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const costPredictionInsertSchema = createInsertSchema(costPredictions).omit({
  id: true,
  createdAt: true,
});

// Pricing Intelligence System Types
export type PricingProduct = typeof pricingProducts.$inferSelect;
export type InsertPricingProduct = z.infer<typeof pricingProductInsertSchema>;
export type PricingRecommendation = typeof pricingRecommendations.$inferSelect;
export type InsertPricingRecommendation = z.infer<typeof pricingRecommendationInsertSchema>;
export type IntelligenceReport = typeof intelligenceReports.$inferSelect;
export type InsertIntelligenceReport = z.infer<typeof intelligenceReportInsertSchema>;
export type WorkloadProfile = typeof workloadProfiles.$inferSelect;
export type InsertWorkloadProfile = z.infer<typeof workloadProfileInsertSchema>;
export type CostPrediction = typeof costPredictions.$inferSelect;
export type InsertCostPrediction = z.infer<typeof costPredictionInsertSchema>;

export const templatePlaceholderInsertSchema = createInsertSchema(templatePlaceholders).omit({
  id: true,
  createdAt: true,
});

export const jobQueueInsertSchema = createInsertSchema(jobQueue).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Type exports for book generation system
export type BookGenerationJob = typeof bookGenerationJobs.$inferSelect;
export type InsertBookGenerationJob = z.infer<typeof bookGenerationJobInsertSchema>;
export type BookTemplate = typeof bookTemplates.$inferSelect;
export type InsertBookTemplate = z.infer<typeof bookTemplateInsertSchema>;
export type TemplatePlaceholder = typeof templatePlaceholders.$inferSelect;
export type InsertTemplatePlaceholder = z.infer<typeof templatePlaceholderInsertSchema>;
export type JobQueue = typeof jobQueue.$inferSelect;
export type InsertJobQueue = z.infer<typeof jobQueueInsertSchema>;

// ============================================================================
// TELEMETRY & AI ANALYTICS SYSTEM
// ============================================================================

// Usage Events - Track all resource consumption in real-time
export const usageEvents = pgTable("usage_events", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id, { onDelete: "cascade" }),
  storyId: integer("story_id").references(() => stories.id, { onDelete: "cascade" }),
  eventType: usageEventTypeEnum("event_type").notNull(),
  resourceName: varchar("resource_name").notNull(), // e.g. "gpt-4o", "dall-e-3", "tts-1"
  quantity: decimal("quantity").notNull(), // tokens, images, seconds, bytes, etc.
  unitCost: decimal("unit_cost").notNull(), // cost per unit in USD
  totalCost: decimal("total_cost").notNull(), // quantity * unit_cost
  metadata: jsonb("metadata"), // request details, model params, etc.
  sessionId: varchar("session_id"), // group related events
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_usage_events_user").on(table.userId),
  index("idx_usage_events_story").on(table.storyId),
  index("idx_usage_events_type").on(table.eventType),
  index("idx_usage_events_created").on(table.createdAt),
  index("idx_usage_events_session").on(table.sessionId)
]);

// Cost Metrics - Aggregated cost data for fast querying
export const costMetrics = pgTable("cost_metrics", {
  id: serial("id").primaryKey(),
  metricType: costMetricTypeEnum("metric_type").notNull(),
  timeWindow: varchar("time_window").notNull(), // "1h", "1d", "1w", "1m"
  windowStart: timestamp("window_start").notNull(),
  windowEnd: timestamp("window_end").notNull(),
  totalCost: decimal("total_cost").notNull(),
  totalQuantity: decimal("total_quantity").notNull(),
  averageUnitCost: decimal("average_unit_cost").notNull(),
  uniqueUsers: integer("unique_users"),
  peakUsage: decimal("peak_usage"), // highest usage in window
  metadata: jsonb("metadata"), // breakdown by model, user segment, etc.
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_cost_metrics_type").on(table.metricType),
  index("idx_cost_metrics_window").on(table.timeWindow, table.windowStart),
  unique("unique_cost_metric").on(table.metricType, table.timeWindow, table.windowStart)
]);

// Pricing Adjustments - AI recommendations and approval workflow
export const pricingAdjustments = pgTable("pricing_adjustments", {
  id: serial("id").primaryKey(),
  agentRunId: integer("agent_run_id").references(() => agentRuns.id),
  productType: varchar("product_type").notNull(), // subscription, gift, addon
  productId: varchar("product_id"), // stripe price ID or internal product ID
  currentPrice: decimal("current_price").notNull(),
  suggestedPrice: decimal("suggested_price").notNull(),
  priceChange: decimal("price_change").notNull(), // suggestedPrice - currentPrice
  priceChangePercent: decimal("price_change_percent").notNull(),
  confidence: decimal("confidence").notNull(), // 0-1 AI confidence score
  reasoning: text("reasoning").notNull(), // AI explanation
  expectedImpact: jsonb("expected_impact"), // revenue, margin, conversion estimates
  guardrails: jsonb("guardrails"), // applied constraints
  status: adjustmentStatusEnum("status").default("pending").notNull(),
  reviewedBy: integer("reviewed_by").references(() => users.id),
  reviewedAt: timestamp("reviewed_at"),
  appliedAt: timestamp("applied_at"),
  revertedAt: timestamp("reverted_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_pricing_adjustments_status").on(table.status),
  index("idx_pricing_adjustments_product").on(table.productType, table.productId),
  index("idx_pricing_adjustments_created").on(table.createdAt)
]);

// Experiments - A/B testing framework for pricing and features
export const experiments = pgTable("experiments", {
  id: serial("id").primaryKey(),
  name: varchar("name").notNull().unique(),
  description: text("description"),
  type: varchar("type").notNull(), // "pricing", "feature", "ui"
  status: varchar("status").default("draft").notNull(), // draft, running, paused, completed
  trafficAllocation: decimal("traffic_allocation").default("0.1").notNull(), // 0-1 percentage
  variants: jsonb("variants").notNull(), // [{name: "control", config: {...}}, {name: "test", config: {...}}]
  targetMetric: varchar("target_metric").notNull(), // conversion, revenue, margin
  guardrails: jsonb("guardrails"), // min conversion, max price change
  startedAt: timestamp("started_at"),
  endedAt: timestamp("ended_at"),
  results: jsonb("results"), // final results and winner
  createdBy: integer("created_by").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_experiments_status").on(table.status),
  index("idx_experiments_type").on(table.type),
  index("idx_experiments_created").on(table.createdAt)
]);

// Agent Runs - AI agent execution logs and outputs
export const agentRuns = pgTable("agent_runs", {
  id: serial("id").primaryKey(),
  agentType: agentTypeEnum("agent_type").notNull(),
  version: varchar("version").default("1.0").notNull(),
  inputs: jsonb("inputs").notNull(), // data fed to agent
  outputs: jsonb("outputs").notNull(), // agent recommendations/analysis
  executionTime: integer("execution_time"), // milliseconds
  tokensUsed: integer("tokens_used"), // OpenAI tokens consumed
  cost: decimal("cost"), // execution cost in USD
  confidence: decimal("confidence"), // overall confidence 0-1
  status: varchar("status").default("running").notNull(), // running, completed, failed
  errorMessage: text("error_message"),
  triggeredBy: varchar("triggered_by"), // "schedule", "manual", "threshold"
  createdAt: timestamp("created_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
}, (table) => [
  index("idx_agent_runs_type").on(table.agentType),
  index("idx_agent_runs_status").on(table.status),
  index("idx_agent_runs_created").on(table.createdAt)
]);

// Real-time Snapshots - Materialized aggregates for fast dashboard queries
export const realtimeSnapshots = pgTable("realtime_snapshots", {
  id: serial("id").primaryKey(),
  snapshotType: varchar("snapshot_type").notNull(), // "cost_summary", "usage_summary", "margin_health"
  timeRange: varchar("time_range").notNull(), // "1h", "24h", "7d", "30d"
  data: jsonb("data").notNull(), // pre-computed metrics for instant loading
  lastUpdated: timestamp("last_updated").defaultNow().notNull(),
  validUntil: timestamp("valid_until").notNull(), // cache expiration
}, (table) => [
  index("idx_realtime_snapshots_type").on(table.snapshotType, table.timeRange),
  index("idx_realtime_snapshots_valid").on(table.validUntil),
  unique("unique_snapshot").on(table.snapshotType, table.timeRange)
]);

// Telemetry Insert Schemas
export const usageEventInsertSchema = createInsertSchema(usageEvents).omit({
  id: true,
  createdAt: true,
});

export const costMetricInsertSchema = createInsertSchema(costMetrics).omit({
  id: true,
  createdAt: true,
});

export const pricingAdjustmentInsertSchema = createInsertSchema(pricingAdjustments).omit({
  id: true,
  createdAt: true,
});

export const experimentInsertSchema = createInsertSchema(experiments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const agentRunInsertSchema = createInsertSchema(agentRuns).omit({
  id: true,
  createdAt: true,
  completedAt: true,
});

export const realtimeSnapshotInsertSchema = createInsertSchema(realtimeSnapshots).omit({
  id: true,
  lastUpdated: true,
});

// CMS Enums
export const cmsPageStatusEnum = pgEnum('cms_page_status', ['draft', 'published', 'archived']);
export const cmsAssetTypeEnum = pgEnum('cms_asset_type', ['image', 'document', 'video', 'audio']);
export const cmsContentBlockTypeEnum = pgEnum('cms_content_block_type', ['text', 'html', 'image', 'video', 'cta', 'hero', 'features']);

// CMS Page Templates - layout templates for pages
export const cmsPageTemplates = pgTable("cms_page_templates", {
  id: serial("id").primaryKey(),
  name: varchar("name").notNull(),
  description: text("description"),
  template: text("template").notNull(), // Template code (React/HTML)
  previewImage: varchar("preview_image"), // Template preview
  isActive: boolean("is_active").default(true),
  isDefault: boolean("is_default").default(false),
  fields: jsonb("fields"), // Configurable fields for this template
  createdBy: integer("created_by").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_cms_page_templates_active").on(table.isActive),
  index("idx_cms_page_templates_default").on(table.isDefault),
]);

// CMS Asset Folders - for organizing media files
export const cmsAssetFolders = pgTable("cms_asset_folders", {
  id: serial("id").primaryKey(),
  name: varchar("name").notNull(),
  parentId: integer("parent_id"),
  sortOrder: integer("sort_order").default(0),
  createdBy: integer("created_by").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_cms_asset_folders_parent").on(table.parentId),
  index("idx_cms_asset_folders_order").on(table.sortOrder),
]);

// CMS Pages - for managing website pages
export const cmsPages = pgTable("cms_pages", {
  id: serial("id").primaryKey(),
  slug: varchar("slug").notNull().unique(), // URL path like 'home', 'about', 'features'
  title: varchar("title").notNull(),
  description: text("description"), // Page description/subtitle
  content: text("content").notNull(), // Main page content (HTML/Markdown)
  metaTitle: varchar("meta_title"), // SEO title
  metaDescription: text("meta_description"), // SEO description
  status: cmsPageStatusEnum("status").default("draft"),
  templateId: integer("template_id").references(() => cmsPageTemplates.id),
  parentId: integer("parent_id"),
  sortOrder: integer("sort_order").default(0), // For ordering in navigation
  isHomePage: boolean("is_home_page").default(false),
  showInNavigation: boolean("show_in_navigation").default(true),
  customData: jsonb("custom_data"), // Additional page data
  publishedAt: timestamp("published_at"),
  createdBy: integer("created_by").references(() => users.id).notNull(),
  updatedBy: integer("updated_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_cms_pages_slug").on(table.slug),
  index("idx_cms_pages_status").on(table.status),
  index("idx_cms_pages_navigation").on(table.showInNavigation),
  index("idx_cms_pages_parent").on(table.parentId),
  index("idx_cms_pages_template").on(table.templateId),
  unique("unique_home_page").on(table.isHomePage),
]);

// CMS Assets - media library for images, documents, etc.
export const cmsAssets = pgTable("cms_assets", {
  id: serial("id").primaryKey(),
  filename: varchar("filename").notNull(),
  originalFilename: varchar("original_filename").notNull(),
  mimeType: varchar("mime_type").notNull(),
  fileSize: bigint("file_size", { mode: "number" }).notNull(), // Size in bytes
  assetType: cmsAssetTypeEnum("asset_type").notNull(),
  url: varchar("url").notNull(), // Object storage URL
  thumbnailUrl: varchar("thumbnail_url"), // For images/videos
  alt: text("alt"), // Alt text for accessibility
  caption: text("caption"), // Caption/description
  metadata: jsonb("metadata"), // EXIF data, dimensions, duration, etc.
  folderId: integer("folder_id").references(() => cmsAssetFolders.id),
  tags: text("tags").array(), // Searchable tags
  isPublic: boolean("is_public").default(true),
  uploadedBy: integer("uploaded_by").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_cms_assets_type").on(table.assetType),
  index("idx_cms_assets_folder").on(table.folderId),
  index("idx_cms_assets_public").on(table.isPublic),
  index("idx_cms_assets_uploaded_by").on(table.uploadedBy),
  index("idx_cms_assets_filename").on(table.filename),
]);

// CMS Content Blocks - reusable content components
export const cmsContentBlocks = pgTable("cms_content_blocks", {
  id: serial("id").primaryKey(),
  name: varchar("name").notNull(), // Internal name for editors
  blockType: cmsContentBlockTypeEnum("block_type").notNull(),
  content: text("content").notNull(), // Block content (HTML/text/JSON)
  settings: jsonb("settings"), // Block-specific settings
  isGlobal: boolean("is_global").default(false), // Can be used across multiple pages
  createdBy: integer("created_by").references(() => users.id).notNull(),
  updatedBy: integer("updated_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_cms_content_blocks_type").on(table.blockType),
  index("idx_cms_content_blocks_global").on(table.isGlobal),
]);

// CMS Navigation Items - for managing site navigation
export const cmsNavigationItems = pgTable("cms_navigation_items", {
  id: serial("id").primaryKey(),
  label: varchar("label").notNull(),
  url: varchar("url"), // External URL
  pageId: integer("page_id").references(() => cmsPages.id), // Internal page link
  parentId: integer("parent_id"), // For dropdown menus
  sortOrder: integer("sort_order").default(0),
  isActive: boolean("is_active").default(true),
  openInNewTab: boolean("open_in_new_tab").default(false),
  cssClass: varchar("css_class"), // Custom CSS class
  createdBy: integer("created_by").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_cms_navigation_active").on(table.isActive),
  index("idx_cms_navigation_parent").on(table.parentId),
  index("idx_cms_navigation_order").on(table.sortOrder),
]);

// CMS Insert Schemas
export const cmsPageInsertSchema = createInsertSchema(cmsPages).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const cmsAssetInsertSchema = createInsertSchema(cmsAssets).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const cmsAssetFolderInsertSchema = createInsertSchema(cmsAssetFolders).omit({
  id: true,
  createdAt: true,
});

export const cmsContentBlockInsertSchema = createInsertSchema(cmsContentBlocks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const cmsPageTemplateInsertSchema = createInsertSchema(cmsPageTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const cmsNavigationItemInsertSchema = createInsertSchema(cmsNavigationItems).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Badge System
export const badgeTypeEnum = pgEnum('badge_type', [
  'official_bear_insider',
  'vip',
  'early_adopter',
  'moderator',
  'partner',
  'staff',
  'beta_tester',
  'community_champion',
  'lifetime_member',
  'bearfluencer'
]);

// User Badges - Many-to-many relationship between users and badges
export const userBadges = pgTable("user_badges", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id).notNull(),
  badge: badgeTypeEnum("badge").notNull(),
  awardedBy: integer("awarded_by").references(() => users.id), // Admin who awarded the badge
  awardedAt: timestamp("awarded_at").defaultNow().notNull(),
  revokedAt: timestamp("revoked_at"), // If badge was revoked
  notes: text("notes"), // Optional notes about the badge award
  syncedToStripe: boolean("synced_to_stripe").default(false), // Track Stripe sync status
}, (table) => [
  index("idx_user_badges_user").on(table.userId),
  index("idx_user_badges_badge").on(table.badge),
  index("idx_user_badges_awarded_by").on(table.awardedBy),
  unique("unique_user_badge").on(table.userId, table.badge), // One badge per user per type
]);

// Badge Insert Schema
export const userBadgeInsertSchema = createInsertSchema(userBadges).omit({
  id: true,
  awardedAt: true,
});

// CMS Type Exports
export type CmsPage = typeof cmsPages.$inferSelect;
export type InsertCmsPage = z.infer<typeof cmsPageInsertSchema>;
export type CmsAsset = typeof cmsAssets.$inferSelect;
export type InsertCmsAsset = z.infer<typeof cmsAssetInsertSchema>;
export type CmsAssetFolder = typeof cmsAssetFolders.$inferSelect;
export type InsertCmsAssetFolder = z.infer<typeof cmsAssetFolderInsertSchema>;
export type CmsContentBlock = typeof cmsContentBlocks.$inferSelect;
export type InsertCmsContentBlock = z.infer<typeof cmsContentBlockInsertSchema>;
export type CmsPageTemplate = typeof cmsPageTemplates.$inferSelect;
export type InsertCmsPageTemplate = z.infer<typeof cmsPageTemplateInsertSchema>;
export type CmsNavigationItem = typeof cmsNavigationItems.$inferSelect;
export type InsertCmsNavigationItem = z.infer<typeof cmsNavigationItemInsertSchema>;

// Telemetry Type Exports
export type UsageEvent = typeof usageEvents.$inferSelect;
export type InsertUsageEvent = z.infer<typeof usageEventInsertSchema>;
export type CostMetric = typeof costMetrics.$inferSelect;
export type InsertCostMetric = z.infer<typeof costMetricInsertSchema>;
export type PricingAdjustment = typeof pricingAdjustments.$inferSelect;
export type InsertPricingAdjustment = z.infer<typeof pricingAdjustmentInsertSchema>;
export type Experiment = typeof experiments.$inferSelect;
export type InsertExperiment = z.infer<typeof experimentInsertSchema>;
export type AgentRun = typeof agentRuns.$inferSelect;
export type InsertAgentRun = z.infer<typeof agentRunInsertSchema>;
export type RealtimeSnapshot = typeof realtimeSnapshots.$inferSelect;
export type InsertRealtimeSnapshot = z.infer<typeof realtimeSnapshotInsertSchema>;

// Badge Type Exports
export type UserBadge = typeof userBadges.$inferSelect;
export type InsertUserBadge = z.infer<typeof userBadgeInsertSchema>;

// Admin Documentation Schema
export const adminDocSectionSchema = z.object({
  id: z.string(),
  title: z.string(),
  content: z.string(),
  subsections: z.array(z.object({
    id: z.string(),
    title: z.string(),
    content: z.string(),
  })).optional(),
});

export const adminDocsSchema = z.object({
  overview: adminDocSectionSchema,
  quickStarts: adminDocSectionSchema,
  features: adminDocSectionSchema,
  faq: adminDocSectionSchema,
  support: adminDocSectionSchema,
});

export const adminDocsEmailSchema = z.object({
  recipients: z.array(z.string().email()).optional(),
}).optional();

// Admin Documentation Type Exports
export type AdminDocSection = z.infer<typeof adminDocSectionSchema>;
export type AdminDocs = z.infer<typeof adminDocsSchema>;
export type AdminDocsEmailRequest = z.infer<typeof adminDocsEmailSchema>;

// Printify Integration Tables
export const printifyShops = pgTable("printify_shops", {
  id: integer("id").primaryKey(),
  title: varchar("title", { length: 255 }).notNull(),
  salesChannel: varchar("sales_channel", { length: 100 }).notNull(),
  currency: varchar("currency", { length: 10 }).notNull(),
  isLocked: boolean("is_locked").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const printifyProducts = pgTable("printify_products", {
  id: integer("id").primaryKey(),
  shopId: integer("shop_id").notNull().references(() => printifyShops.id, { onDelete: "cascade" }),
  title: varchar("title", { length: 255 }).notNull(),
  description: text("description"),
  handle: varchar("handle", { length: 255 }),
  blueprintId: integer("blueprint_id").notNull(),
  printProviderId: integer("print_provider_id").notNull(),
  tags: jsonb("tags").$type<string[]>().default([]).notNull(),
  images: jsonb("images").$type<Array<{
    src: string;
    position: string;
    isDefault: boolean;
  }>>().default([]).notNull(),
  variants: jsonb("variants").$type<Array<{
    id: number;
    price: number;
    isEnabled: boolean;
    isDefault: boolean;
    isAvailable: boolean;
  }>>().default([]).notNull(),
  visible: boolean("visible").default(false).notNull(),
  isLocked: boolean("is_locked").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const printifyOrders = pgTable("printify_orders", {
  id: varchar("id", { length: 50 }).primaryKey(),
  shopId: integer("shop_id").notNull().references(() => printifyShops.id, { onDelete: "cascade" }),
  userId: integer("user_id").references(() => users.id, { onDelete: "set null" }),
  status: varchar("status", { length: 50 }).notNull(),
  lineItems: jsonb("line_items").$type<Array<{
    product_id: number;
    variant_id: number;
    quantity: number;
    print_areas: Array<{
      src: string;
      position: string;
    }>;
  }>>().default([]).notNull(),
  shippingAddress: jsonb("shipping_address").$type<{
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    country: string;
    region: string;
    address1: string;
    address2?: string;
    city: string;
    zip: string;
  }>(),
  totalPrice: integer("total_price").notNull(), // in cents
  shippingCost: integer("shipping_cost").default(0).notNull(), // in cents
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const printifyWebhooks = pgTable("printify_webhooks", {
  id: serial("id").primaryKey(),
  shopId: integer("shop_id").references(() => printifyShops.id, { onDelete: "cascade" }),
  webhookId: varchar("webhook_id", { length: 50 }),
  topic: varchar("topic", { length: 100 }).notNull(),
  url: varchar("url", { length: 500 }).notNull(),
  isActive: boolean("is_active").default(true).notNull(),
  secret: varchar("secret", { length: 255 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Cardivo Gift Card Redemptions - Track external Cardivo API gift card redemptions
export const cardivoGiftCardRedemptions = pgTable("cardivo_gift_card_redemptions", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id, { onDelete: "set null" }),
  giftCardNumber: varchar("gift_card_number", { length: 100 }).notNull(), // e.g., CARDIVO04921624
  valueRedeemed: decimal("value_redeemed", { precision: 10, scale: 2 }).notNull(),
  valueOriginal: decimal("value_original", { precision: 10, scale: 2 }),
  valueBalance: decimal("value_balance", { precision: 10, scale: 2 }),
  currency: varchar("currency", { length: 3 }).notNull(),
  email: varchar("email", { length: 255 }).notNull(),
  ipAddress: varchar("ip_address", { length: 45 }),
  description: text("description"),
  purchaseTotal: decimal("purchase_total", { precision: 10, scale: 2 }),
  totalExclGiftCard: decimal("total_excl_gift_card", { precision: 10, scale: 2 }),
  cardivoResponse: jsonb("cardivo_response"), // Full response from Cardivo API
  redemptionUrl: varchar("redemption_url", { length: 500 }), // Gift card summary URL from Cardivo
  status: varchar("status", { length: 50 }).notNull().default("pending"), // pending, successful, failed
  errorMessage: text("error_message"),
  redeemedAt: timestamp("redeemed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_cardivo_redemptions_user").on(table.userId),
  index("idx_cardivo_redemptions_gift_card").on(table.giftCardNumber),
  index("idx_cardivo_redemptions_status").on(table.status),
]);

// Cardivo Gift Card Purchases - Track Cardivo gift card purchases (not subscription gift cards)
export const cardivoGiftCards = pgTable("cardivo_gift_cards", {
  id: serial("id").primaryKey(),
  designId: varchar("design_id", { length: 100 }).notNull(), // Cardivo design ID
  amount: decimal("amount", { precision: 10, scale: 2 }).notNull(), // Gift card value
  currency: varchar("currency", { length: 3 }).notNull().default("USD"),
  purchaserUserId: integer("purchaser_user_id").references(() => users.id, { onDelete: "set null" }), // Nullable for guest purchases
  purchaserEmail: varchar("purchaser_email", { length: 255 }).notNull(),
  recipientName: varchar("recipient_name", { length: 255 }).notNull(),
  recipientEmail: varchar("recipient_email", { length: 255 }).notNull(),
  message: text("message"), // Optional personal message
  paymentIntentId: varchar("payment_intent_id", { length: 255 }).notNull().unique(), // Stripe PaymentIntent ID
  cardivoGiftCardNumber: varchar("cardivo_gift_card_number", { length: 100 }), // From Cardivo API after issuing
  cardivoSummaryUrl: varchar("cardivo_summary_url", { length: 500 }), // Gift card summary URL from Cardivo
  status: varchar("status", { length: 50 }).notNull().default("pending_payment"), // pending_payment, issuing, issued, failed
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_cardivo_gift_cards_payment_intent").on(table.paymentIntentId),
  index("idx_cardivo_gift_cards_purchaser").on(table.purchaserUserId),
  index("idx_cardivo_gift_cards_status").on(table.status),
]);

// Playlist Schemas
export const playlistInsertSchema = createInsertSchema(playlists).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const playlistStoryInsertSchema = createInsertSchema(playlistStories).omit({
  id: true,
  addedAt: true,
});

// Playlist Type Exports
export type Playlist = typeof playlists.$inferSelect;
export type InsertPlaylist = z.infer<typeof playlistInsertSchema>;
export type PlaylistStory = typeof playlistStories.$inferSelect;
export type InsertPlaylistStory = z.infer<typeof playlistStoryInsertSchema>;

// Story Audio Segment Schemas
export const storyAudioSegmentInsertSchema = createInsertSchema(storyAudioSegments).omit({
  id: true,
  createdAt: true,
});

export type StoryAudioSegment = typeof storyAudioSegments.$inferSelect;
export type InsertStoryAudioSegment = z.infer<typeof storyAudioSegmentInsertSchema>;

// Email Sequence Schemas
export const emailSequenceInsertSchema = createInsertSchema(emailSequences).omit({
  id: true,
  createdAt: true,
});

export type EmailSequence = typeof emailSequences.$inferSelect;
export type InsertEmailSequence = z.infer<typeof emailSequenceInsertSchema>;

// Email Template Variant Schemas
export const emailTemplateVariantInsertSchema = createInsertSchema(emailTemplateVariants).omit({
  id: true,
  createdAt: true,
});

export type EmailTemplateVariant = typeof emailTemplateVariants.$inferSelect;
export type InsertEmailTemplateVariant = z.infer<typeof emailTemplateVariantInsertSchema>;

// Printify Schemas
export const printifyShopInsertSchema = createInsertSchema(printifyShops).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const printifyProductInsertSchema = createInsertSchema(printifyProducts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const printifyOrderInsertSchema = createInsertSchema(printifyOrders).omit({
  createdAt: true,
  updatedAt: true,
});

export const printifyWebhookInsertSchema = createInsertSchema(printifyWebhooks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Printify Type Exports
export type PrintifyShop = typeof printifyShops.$inferSelect;
export type InsertPrintifyShop = z.infer<typeof printifyShopInsertSchema>;
export type PrintifyProduct = typeof printifyProducts.$inferSelect;
export type InsertPrintifyProduct = z.infer<typeof printifyProductInsertSchema>;
export type PrintifyOrder = typeof printifyOrders.$inferSelect;
export type InsertPrintifyOrder = z.infer<typeof printifyOrderInsertSchema>;
export type PrintifyWebhook = typeof printifyWebhooks.$inferSelect;
export type InsertPrintifyWebhook = z.infer<typeof printifyWebhookInsertSchema>;

// Cardivo Gift Card Redemption Schemas
export const cardivoGiftCardRedemptionInsertSchema = createInsertSchema(cardivoGiftCardRedemptions).omit({
  id: true,
  createdAt: true,
});

export type CardivoGiftCardRedemption = typeof cardivoGiftCardRedemptions.$inferSelect;
export type InsertCardivoGiftCardRedemption = z.infer<typeof cardivoGiftCardRedemptionInsertSchema>;

// Cardivo Gift Card Purchase Schemas
export const cardivoGiftCardInsertSchema = createInsertSchema(cardivoGiftCards).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type CardivoGiftCard = typeof cardivoGiftCards.$inferSelect;
export type InsertCardivoGiftCard = z.infer<typeof cardivoGiftCardInsertSchema>;

// Subscription Products Schemas
export const subscriptionProductInsertSchema = createInsertSchema(subscriptionProducts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type SubscriptionProduct = typeof subscriptionProducts.$inferSelect;
export type InsertSubscriptionProduct = z.infer<typeof subscriptionProductInsertSchema>;

// User Subscription Products Schemas
export const userSubscriptionProductInsertSchema = createInsertSchema(userSubscriptionProducts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type UserSubscriptionProduct = typeof userSubscriptionProducts.$inferSelect;
export type InsertUserSubscriptionProduct = z.infer<typeof userSubscriptionProductInsertSchema>;

// Mobile Devices Schemas
export const mobileDeviceInsertSchema = createInsertSchema(mobileDevices).omit({
  id: true,
  createdAt: true,
});

export type MobileDevice = typeof mobileDevices.$inferSelect;
export type InsertMobileDevice = z.infer<typeof mobileDeviceInsertSchema>;

// Mobile API Keys Schemas
export const mobileApiKeyInsertSchema = createInsertSchema(mobileApiKeys).omit({
  id: true,
  createdAt: true,
});

export type MobileApiKey = typeof mobileApiKeys.$inferSelect;
export type InsertMobileApiKey = z.infer<typeof mobileApiKeyInsertSchema>;

// Mobile API Key Usage Schemas
export const mobileApiKeyUsageInsertSchema = createInsertSchema(mobileApiKeyUsage).omit({
  id: true,
});

export type MobileApiKeyUsage = typeof mobileApiKeyUsage.$inferSelect;
export type InsertMobileApiKeyUsage = z.infer<typeof mobileApiKeyUsageInsertSchema>;
